// Third party
import React, { Component, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Redirect, withRouter, RouteComponentProps, Route, Switch, generatePath } from 'react-router';
import { LocationState } from 'history';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { SortingRule } from 'react-table';
import qs from 'qs';

// Namespaces
import * as ProposalNS from '../../../modules/proposal/Proposal';

// Constants and types
import { SortingField, modelType } from '../../../types/data';
import {
  PERFORMANCE_CHART,
  COMPARISON_CARD_WIDTH,
  NAV_PATHS,
  COMPARISON_NAV_PATHS,
  COMPARISON_ITEM_TYPES,
  BOND_TAG,
  COMPARISON_CATEGORIES,
} from '../../../utils/constants';
import ThemeMode from '@/types/theme-mode';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { CurrentContact } from '../../../redux/modules/types/contacts';
import { FundStoreState } from '../../../redux/modules/types/fund-store';
import { StockStoreState } from '../../../redux/modules/types/store-stock';
import {
  addPortfoliosToComparison,
  applyMarkupToCell,
  clearAllMarkup,
  clearMarkup,
  clonePortfolio,
  closeComparison,
  combineIntoPortfolio,
  loadComparison,
  loadNewPortfolio,
  removeItem,
  replacePortfolioInComparison,
  rerunComparison,
  saveComparison,
  selectComparisonItem,
  setChartOptions,
  setComparisonShouldForceMonthlyData,
  setComparisonContact,
  setComparisonName,
  setExpandPortfolioItem,
  setRiskPeriod,
  setSelectedCategories,
  setTab,
  shareComparison,
  updateSelectedCategories,
  toggleShortHistory,
  cloneBenchmark,
  replaceBenchmarkInComparison,
  calculateIncomeStream,
  showBaseCurrencies,
  setMonthEndDataOption,
  addRationaleAnswers,
  addItemToGroup,
  removeItemFromGroup,
  ADD_PORTFOLIOS_TO_COMPARISON,
  SET_IS_RERUNNING,
  LOAD_COMPARISON,
  addItemAndRecalculateComparison,
  addGroupToComparison,
  updateComparisonGroup,
  setComparisonItemsFilter,
  changeItemId,
  addCategoryBenchmark,
  unselectComparisonItem,
  removeMissingComparisonData,
  createComparison,
  getComparisonById,
  validateComparisonToken,
  skipEmptyState,
  toggleCategory,
  ADD_FUND_TO_COMPARISON,
  ADD_PORTFOLIO_TO_COMPARISON,
  ADD_STOCK_TO_COMPARISON,
  ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON,
  ADD_BENCHMARK_TO_COMPARISON,
  setOriginPath,
  attachRiskProfile,
  openLoadClientPortfoliosModal,
  closeLoadClientPortfoliosModal,
  setCommonInceptionItem,
  ADD_FUNDS_TO_COMPARISON,
  createOmniExport,
  getItemHoldingsData,
  PROCESSING_COMPARISON_ITEM_ACTION,
  updateAccountSets,
  HoldingsData,
} from '../../../redux/modules/comparison';
import { resetSearch } from '../../../redux/modules/advanced-search';
import {
  loadPortfolioForEdit,
  resetPortfolio,
  savePortfolio,
  setPortfolioContact,
  saveModelCopyToDraftPortfolio,
  setPortfolioEditorIsCreatingModel,
  setShowTaxDetails,
} from '../../../redux/modules/edit-portfolio';
import { getPortfolioById, getModelsPortfolios } from '../../../redux/modules/portfolios';
import {
  loadContactPortfolios,
  getContactPortfolios,
  initLoadingPortfolios,
  getContactById,
  sendPostMessageGetContactsAndPortfolios,
  setCurrentContact,
} from '../../../redux/modules/contacts';
import { getKycQuestionnaires } from '../../../redux/modules/kyc-questionnaire';
import { loadOmniRiskProfilesQuestionnaire } from '@/redux/modules/omni-risk-profiles';
import {
  getQuestionnaireAnswersById,
  getQuestionnaires,
  saveQuestionnaireAnswers,
  updateQuestionnaireAnswers,
  deleteQuestionnaireAnswers,
} from '../../../redux/modules/questionnaire';
import { loadBenchmarkForEdit, resetCurrentContact, togglePortfolioEditor } from '../../../redux/modules/common/actionCreators';
import { saveBenchmark } from '../../../redux/modules/edit-benchmark';
import { getBenchmarkPortfolio } from '../../../redux/modules/comparison-benchmarks/benchmark-portfolios';
import { resetProposal, loadProposalTemplates, setProposalTemplate } from '../../../redux/modules/proposal-v2';
import { RateLimitType } from '../../../redux/modules/types/rate-limits';
import { consumeLimit } from '../../../redux/modules/rate-limits';
import { selectIsLoadingEvery, selectIsLoadingSome, startLoaderAction, stopLoaderAction } from '../../../redux/modules/ui/loaders';
import { selectShelfStatusedComparison } from '../../../redux/modules/shelf-management/selectors';
import { selectComparisonWithFilteredItems } from '../../../redux/modules/comparison/selectors';
import { shouldClosePortfolioEditor } from '@/redux/modules/ai-statement-reader-multi-portfolio';

// Components
import ComparisonQuickSearch from '@/views/containers/comparison/misc/ComparisonQuickSearch';
import { PrimaryButton, Button, CloseButton } from '../../components/buttons';
import {
  SavePortfolioModal,
  DiscardPortfolioModal,
  DiscardComparisonModal,
  LoadPortfoliosModal,
  ProposalRequirementsModal,
  EditPortfolioModal,
  AssignPortfolioModal,
  NoAssetClassModal,
  CreateQuestionnaire,
  ProposalExportRequirementsModal,
  SuitabilityAnalysisModal,
  ESGNoDataModal,
  ComparisonMissingItemsModal,
  WarningModal,
  MixedCurrencyModal,
  ExportPresentationModal,
  ProposalExportRequirementsModalDisplayMode,
} from '../../components/modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import ComparisonTable, { Props as ComparisonTableProps } from './ComparisonTable/ComparisonTable';
import AccessibleComparisonTable from './AccessibleComparisonTable/AccessibleComparisonTable';
import ComparisonSaveButton from './ComparisonSaveButton';
import { PrimaryNavigation } from '../../components/navigation';
import RiskProfileView from './RiskProfile/RiskProfileView';
import { MarkupToolbar } from '../../components/markup-tools';
import EmptyComparison from './EmptyComparison';
import BMOGIFView from './BMOGIF/BMOGIFView';
import BMOGIFComparisonTable from './BMOGIF/BMOGIFComparisonTable';
import RateLimitedComponent from '../../components/RateLimitedComponent';
import IncomeStreamTable from './IncomeStreamTable/IncomeStreamTable';
import { IncomeChart } from '../../components/charts';
import {
  ComparisonTopBarNavigationResponsive,
  ComparisonTopBarContactButton,
  ComparisonTopBarContactMenuItem,
  ComparisonTopBarSettingsButton,
  ComparisonTopBarSettingsMenuItem,
  ComparisonTopBarSettingsModal,
  ComparisonTopBarStatusMenuItem,
  ComparisonTopBarRationaleMenuItem,
  ComparisonTopBarSuitabilityAnalysisMenuItem,
} from './ComparisonTopBarNavigation';
import { Tooltip } from '../../components/tooltips';
import DeleteComparisonItemGroupModal from '../../components/modals/comparison-modals/DeleteComparisonItemGroupsModal';
import DelegateBanner from '../auth/DelegateBanner';
import { AnnouncementBanner } from '../announcements';
import AddComparisonGroupModal from '../../components/modals/add-comparison-group-modal/AddComparisonGroupModal';
import { ManageAccountSetsModal } from '@/views/components/modals/account-sets';
import ComparisonFocusOverlay from './ComparisonFocusOverlay';
import ESGView from './ESG/ESGView';
import HistoricalScenarios from './HistoricalScenarios';
import { TaxAnalysisView } from './TaxAnalysis';
import { ScrollToTop } from '@/views/components/misc';
import LinkHeader from '@/views/components/link/LinkHeader';
import LinkLogo from '@/views/components/link/LinkLogo';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import ProtectedView from './ProtectedView';
import { ComparisonActionButtons } from './ComparisonActionButtons';
import { ChartContainer } from './Charts';
import PortfolioAiStatementReaderModal from '@/views/components/modals/portfolio-ai-statement-reader/PortfolioAIStatementReaderModal';
import MultiPortfolioAiStatementReaderModal from '@/views/components/modals/multi-portfolio-ai-statement-reader/MultiPortfolioAiStatementReaderModal';
import AccountSet from '@/types/comparison-account-set';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';
import { useComparisonTabSelection } from '@/hooks/useComparisonTabSelection';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../utils/3rd-party/heap';
import localStorage from '@/utils/local-storage';
import { canAccessAIStatementReader, canAccessMultiPortfoliosStatementReader, canAccessTaxAnalysis } from '@/utils/feature-access';
import { formatLocalizedString, formatMessage, formatMessageWithLocale, FormattedMessage, LOCALE } from '../../../utils/i18n';
import notification from '../../../utils/notification';
import {
  getFilteredItems,
  comparisonIsReadOnly,
  getDuplicateFields,
  comparisonHasItems,
  getComparisonContactName,
  comparisonIsCompliance,
  comparisonIsEditable,
  comparisonHasMissingOrModifiedItems,
} from '../../../utils/comparison';
import { showError } from '../../../utils/errors';
import { MARKUP_TOOLS, selectCategoryFields, getAvailableFields, fieldIsSelected } from '../../../utils/markup-tools';
import { userHasPermissionEnabled, userHasPermission, PERMISSIONS, userHasPermissions } from '../../../utils/permissions';
import { getTotalPortfolioAllocation, HOLDING_TYPES, portfolioIsNew, PORTFOLIO_TYPES } from '../../../utils/portfolios';
import { timeSince } from '../../../utils/time';
import {
  getIncomeStartDate,
  hasIncomeStreamScenarios,
  filterIncomeStreamItems,
  doesItemHaveStocksOrBonds,
  getIncomeEndDate,
} from '../../../utils/income-stream';
import {
  generateFundFromCustomYieldConfig,
  clonePortfolioAsProxy,
  isEnterpriseModel,
  loadPortfoliosIfHoldingsAreMissing,
} from '../../../utils/portfolio/utils';
import dom from '../../../utils/dom';
import disclaimers from '../../../utils/disclaimers';
import { getActiveQuestionnaire, QUESTIONNAIRE_TYPES } from '../../../utils/questionnaire';
import { LOAD_PORTFOLIOS_TABS } from '@/views/components/modals/comparison-modals/LoadPortfoliosModal';
import { CURRENCY } from '@/utils/currency';
import domUtils from '../../../utils/dom';
import { getOmniLocale } from '@/utils/locale';
import {
  getItemsOutsideRiskProfileRange,
  validateRiskProfileRequirementsForExport,
  validateRationaleQuestionnaireRequirements,
} from './comparison-builder/utils/comparison-export-requirements';
import { logHistoricalScenariosUsage } from '@/services/usage-log';

// Config
import { getComparisonCategories } from './utils/comparison-table-config';
import { ProposalTemplateUtils } from '../../../modules/proposal/ProposalTemplateUtils';
import { ComparisonItemsFilter } from '../../../redux/modules/types/comparison';

// Enums

// Types
import Dictionary from '@/types/dictionary';
import Portfolio from '@/types/portfolio';
import ComparisonItem from '@/types/comparison-item';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import Locale from '@/types/locale';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Questionnaire from '@/types/questionnaire';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import GenericContact from '@/types/generic-contact';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import Comparison from '@/types/comparison';
import ComparisonContact from '@/types/comparison-contact';
import Contact from '@/types/contact';
import Nullable from '@/types/nullable';
import SharedComparisonInfos from '@/types/shared-comparison-infos';
import SelectedCategories from '@/types/selected-categories';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import ComparisonChartOptions from '@/types/comparison-chart-options';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import ComparisonGroupProduct from '@/types/comparison-group-product';
import User from '@/types/user';
import AppMode from '@/types/app-mode';
import PaginatedPortfolios from '@/types/paginated-portfolios';
import NavigationItem from '@/types/navigation-item';
import ComparisonItemGroup from '@/types/comparison-item-group';
import GrowthChartOptions from '@/types/growth-chart-options';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import ComparisonHolding from '@/types/comparison-holding';
import IncomeChartOptions from '@/types/income-chart-options';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import RiskProfileItem from '@/types/risk-profile-item';
import UserPreferences from '@/types/user-preferences';
import { UUID } from '@/types/global';
import { StateOmniRiskProfileConfig } from '@/types/risk-profiles/omni/omni-risk-profile-context-types';
import OmniRiskProfilesQuestionnaire from '@/types/risk-profiles/omni/omni-risk-profiles-questionnaire';
import StatusFeedbackModal from '@/views/components/modals/StatusFeedbackModal';
import { Status } from '@/types/status';
import { GenericHouseHold } from '@/types/households';
import HistoricalScenariosTable from './HistoricalScenarios/HistoricalScenariosTable';
import TaxAnalysisTable from './TaxAnalysis/components/TaxAnalysisImplicationsTable/TaxAnalysisTable';
import { doesItemHasAlt } from '@/utils/historical-scenarios';
import { NoAssetClass } from '@/types/no-asset-class';
import { ComparisonNavigationTab } from '@/enums/Comparison';
import { ApiRequestConfig } from '@/utils/api';
import { filterTaxAnalysisItems } from '@/utils/tax-analysis';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/enums/FeatureFlags';

enum SuitabilityAnalysisModalDisplayMode {
  DEFAULT = 'default',
  HIDDEN = 'hidden',
  PROPOSAL = 'proposal',
}

type StateProps = {
  comparisonIsLoading: boolean;
  isProcessing: boolean;
  editedPortfolio: Portfolio;
  isPortfolioDirty: boolean;
  isPostMessageActive: boolean;
  canPortfolioSave: boolean;
  editedComparisonItem?: ComparisonItem;
  selectedComparisonItems: ComparisonItem[];
  modelPortfolios: Portfolio[];
  myModelsPortfolios: Portfolio[];
  enterpriseModelsPortfolios: Portfolio[];
  enterprise: EnterpriseDetails;
  locale: Locale;
  kycQuestionnaires: KycQuestionnaire[];
  rationaleQuestionnaires: Questionnaire[];
  rationaleCompleted: boolean;
  rationaleAnswers?: QuestionnaireAnswers;
  fundStore: FundStoreState;
  stockStore: StockStoreState;
  nextModelsToken?: string;
  nextMyModelsToken?: string;
  nextEnterpriseModelsToken?: string;
  selectedContact?: {
    contact: GenericContact;
    portfolios?: Portfolio[];
    hasMore: {
      portfolios: boolean;
    };
    nextContactPortfoliosToken?: string;
    nextContactComparisonsToken?: string;
  };
  proposalTemplate?: ProposalNS.BaseProposalTemplate;
  proposalTemplates: ProposalNS.ProposalTemplateV2[];
  isSaving: boolean;
  isEditVisible: boolean;
  isEditPinned: boolean;
  isEditReadOnly: boolean;
  editedScenario?: IncomeStreamScenario;
  currentContact?: CurrentContact;
  isComparingPortfolios: boolean;
  suitabilityAnalysisCompleted: boolean;
  isRerunning: boolean;
  itemsFilter: string[];
  isAddingToComparison: boolean;
  emptyStateIsSkipped: boolean;
  tab?: string;
  originPath?: Location;
  userPreferences?: UserPreferences;
  showLoadClientPortfoliosModal: boolean;
  contacts: GenericContact[];
  shouldUpdateCommonInception: boolean;
  presentationTemplates: string[];
  postMessagePortfolios: Portfolio[];
  shouldCloseEditor: boolean;
};

type DispatchProps = {
  resetSearch: Function;
  removeItem: (itemId: string) => void;
  createComparison: (contact?: GenericContact, household?: GenericHouseHold) => Promise<Comparison>;
  getComparisonById: (id: string, contact?: ComparisonContact) => Promise<Comparison>;
  skipEmptyState: () => void;
  validateComparisonToken: (token: string) => Promise<string>;
  loadPortfolioForEdit: (
    portfolio: Portfolio,
    comparisonItem?: ComparisonItem,
    isPinned?: boolean,
    isDirty?: boolean,
    isReadOnly?: boolean,
    forceShowTaxDetails?: boolean,
  ) => Promise<void>;
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
    options?: ApiRequestConfig,
  ) => Promise<Portfolio>;
  clonePortfolio: (portfolio: Portfolio) => Promise<void>;
  resetPortfolio: () => void;
  savePortfolio: (portfolio: Portfolio, contact?: Contact) => Promise<Portfolio>;
  replacePortfolioInComparison: (portfolio: Portfolio, replaceId?: string, isIncomeTab?: boolean) => Promise<void>;
  resetProposal: () => void;
  getModelsPortfolios: (type?: modelType, query?: string, sort?: SortingRule[], nextToken?: string) => Promise<Portfolio[]>;
  loadNewPortfolio: (contact?: GenericContact, createItem?: boolean) => Promise<void>;
  saveComparison: (isNewlyAssigned?: boolean) => Promise<Comparison>;
  setComparisonName: (name: string) => void;
  setComparisonContact: (contact: Nullable<GenericContact>) => void;
  setCurrentContact: (contact: GenericContact) => void;
  loadComparison: (comparison: Comparison) => Promise<Comparison>;
  loadProposalTemplates: () => Promise<ProposalNS.ProposalTemplate[]>;
  rerunComparison: (comparison: Comparison) => Promise<Comparison>;
  shareComparison: (infos: SharedComparisonInfos) => Promise<Nullable<string>>;
  selectComparisonItem: (item: ComparisonItem, unselectOthers?: boolean, position?: number) => void;
  setSelectedCategories: (selectedCategories?: SelectedCategories) => void;
  setTab: (path?: string) => void;
  applyMarkupToCell: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, markupTool: string) => void;
  clearAllMarkup: () => void;
  clearMarkup: (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string) => void;
  updateSelectedCategories: (previousCategories: ComparisonCategory[], currentCategories: ComparisonCategory[], duplicateFields?: string[]) => void;
  getKycQuestionnaires: () => Promise<KycQuestionnaire[]>;
  loadOmniRiskProfilesQuestionnaire: (formId: UUID) => Promise<OmniRiskProfilesQuestionnaire>;
  getQuestionnaires: (reducerName: QUESTIONNAIRE_TYPES) => Promise<Questionnaire[]>;
  getQuestionnaireAnswersById: (reducerName: QUESTIONNAIRE_TYPES, id?: string) => Promise<QuestionnaireAnswers>;
  setChartOptions: (options: ComparisonChartOptions, chart: PERFORMANCE_CHART) => void;
  setComparisonShouldForceMonthlyData: (force?: boolean) => void;
  setExpandPortfolioItem: (item: ComparisonItem, isExpanded: boolean) => void;
  closeComparison: (comparison: Comparison) => void;
  combineIntoPortfolio: () => void;
  addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  loadContactPortfolios: (contact: GenericContact) => Promise<void>;
  getContactPortfolios: (query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => Promise<Portfolio[]>;
  initLoadingPortfolios: () => void;
  resetCurrentContact: () => void;
  setPortfolioContact: (contact: GenericContact) => void;
  toggleShortHistory: (toggled?: boolean) => void;
  loadBenchmarkForEdit: (benchmarkPortfolio: BenchmarkPortfolio, item?: ComparisonItem) => void;
  getBenchmarkPortfolio: (item: ComparisonItem) => Promise<BenchmarkPortfolio>;
  cloneBenchmark: (benchmarkPortfolio: BenchmarkPortfolio) => Promise<void>;
  saveBenchmark: (benchmarkPortfolio: BenchmarkPortfolio) => Promise<BenchmarkPortfolio>;
  replaceBenchmarkInComparison: (benchmarkPortfolio: BenchmarkPortfolio, tempId?: string) => void;
  setProposalTemplate: (template: ProposalNS.ProposalTemplateV2, setDefault?: boolean) => void;
  calculateIncomeStream: (startDate: Date, endDate: Date) => void;
  showBaseCurrencies: (useBaseCurrency: boolean) => Promise<void>;
  setMonthEndDataOption: (monthEndOptionValue?: PERIOD_END_DATA_OPTIONS) => void;
  togglePortfolioEditor: (isVisible: boolean, isPinned?: boolean, isFocused?: boolean, isReadOnly?: boolean) => void;
  consumeLimit: (limitType: RateLimitType) => void;
  saveQuestionnaireAnswers: (
    reducerName: QUESTIONNAIRE_TYPES,
    questionnaireAnswers: QuestionnaireAnswers,
    questionnaire: Questionnaire,
    comparisonId: string | null,
  ) => Promise<QuestionnaireAnswers>;
  updateQuestionnaireAnswers: (
    reducerName: QUESTIONNAIRE_TYPES,
    id: string,
    questionnaireAnswers: QuestionnaireAnswers,
    questionnaire: Questionnaire,
  ) => Promise<QuestionnaireAnswers>;
  deleteQuestionnaireAnswers: (reducerName: QUESTIONNAIRE_TYPES, id: string) => Promise<QuestionnaireAnswers>;
  saveModelCopyToDraftPortfolio: (portfolio: Portfolio) => Promise<Portfolio>;
  addRationaleAnswers: (answersId: string) => void;
  getContactById: (id: string) => Promise<GenericContact>;
  addItemToGroup: (itemId: string, groupId: string, dollars: number, fee?: number) => void;
  removeItemFromGroup: (itemId: string, groupId: string) => void;
  updateComparisonGroup: (id: string, name: string, products: ComparisonGroupProduct[], color?: string) => void;
  addItemAndRecalculateComparison: (item: ComparisonItem) => void;
  addGroupToComparison: (name: string, products: ComparisonGroupProduct[], color?: string) => void;
  setComparisonItemsFilter: (filter: string[]) => void;
  changeItemId: (originalId: string, newId: string) => void;
  addCategoryBenchmark: (item: ComparisonItem) => Promise<void>;
  unselectComparisonItem: (item: ComparisonItem) => void;
  removeMissingComparisonData: (comparison: Comparison) => Promise<Comparison>;
  toggleCategory: (category: ComparisonCategory) => void;
  setOriginPath: (originPath: LocationState) => void;
  attachRiskProfile: (profile: RiskProfileItem) => Promise<void>;
  openLoadClientPortfoliosModal: () => void;
  closeLoadClientPortfoliosModal: () => void;
  setCommonInceptionItem: () => void;
  sendPostMessageGetContactsAndPortfolios: () => void;
  createOmniExport: (comparisonId: string, contactId?: string) => Promise<{ comparisonExportId: string }>;
  getItemHoldingsData: (holdings: ComparisonHolding[]) => Promise<HoldingsData>;
  startLoaderAction: (loaderType: string) => void;
  stopLoaderAction: (loaderType: string) => void;
  setPortfolioEditorIsCreatingModel: (isCreatingModel?: boolean) => void;
  setShowTaxDetails(show: boolean): void;
  updateAccountSets: (accountSets: AccountSet[]) => void;
};

type ComparisonBuilderProps = DispatchProps &
  StateProps &
  RouteComponentProps & {
    comparison: Comparison;
    currentHousehold?: GenericHouseHold;
    rawComparison: Comparison;
    readOnly?: boolean;
    hasMarkup?: boolean;
    isLightVersion?: boolean;
    user: User;
    isFFEnabled: (flag: FEATURE_FLAGS) => boolean;
    tabSelections: Record<string, number>;
    updateTabSelection: (categoryKey: string, value: number) => void;
  };

type State = {
  redirect?: string;
  showAddGroupModal: boolean;
  showManageAccountSetsModal: boolean;
  showDeleteGroupModal: boolean;
  showSavePortfolioModal: boolean;
  showDiscardPortfolioModal: boolean;
  showDiscardComparisonModal: boolean;
  showLoadModelPortfoliosModal: boolean;
  showProposalRequirementsModal: boolean;
  showNoAssetClassModal?: boolean;
  noAssetClassModalTab?: NoAssetClass;
  showComparisonMissingItemsModal: boolean;
  showAssignPortfolioModal: boolean;
  showMissingLinkedContactModal: boolean;
  showExportPresentationModal: boolean;
  proposalExportRequirementsDisplayMode: ProposalExportRequirementsModalDisplayMode[];
  showDeleteRationaleAnswersModal: boolean;
  portfolioToSave?: Portfolio;
  portfolioSaveToContact?: boolean;
  groupItemsToDelete?: ComparisonItem[];
  markupTool?: string;
  duplicateFields?: string[];
  previewMarkup?: boolean;
  sorting: SortingRule[];
  queries: {
    portfolios: string;
    contactPortfolios: string;
  };
  loading: {
    portfolios: boolean;
  };
  proposalRequirements?: ProposalNS.FormattedProposalTemplateRequirement[];
  pendingPortfolios: Portfolio[];
  showSuitabilityModal: boolean;
  suitabilityAnalysisModalDisplayMode: SuitabilityAnalysisModalDisplayMode;
  deleteGroupsCallback?: () => void;
  groupToEdit?: ComparisonItem;
  showESGNoDataModal: boolean;
  comparisonMissingItems?: Comparison;
  redirectPath?: string;
  showCloseComparisonModal: boolean;
  comparisonPath: string;
  isExporting: boolean;
  showPortfolioUploadModal: boolean;
  showPortfolioAiStatementReaderModal: boolean;
  showMixedCurrencyModal: boolean;
  isConvertingMixedCurrency: boolean;
  portfolioNamesOutsideRiskProfileRange: string[];
  timeSpendUsingHS: number;
  timeStartUsingHS: number;
};

class ComparisonBuilder extends Component<ComparisonBuilderProps, State> {
  private comparisonTable = React.createRef<HTMLDivElement>();

  private popoverContainerRef = React.createRef<HTMLDivElement>();

  readonly DEFAULT_COMPARISON_NAME = formatMessage('Comparison.DefaultName');

  readonly state: State = {
    showAddGroupModal: false,
    showManageAccountSetsModal: false,
    showDeleteGroupModal: false,
    showSavePortfolioModal: false,
    showDiscardPortfolioModal: false,
    showDiscardComparisonModal: false,
    showLoadModelPortfoliosModal: false,
    showProposalRequirementsModal: false,
    showNoAssetClassModal: false,
    showComparisonMissingItemsModal: false,
    showAssignPortfolioModal: false,
    showDeleteRationaleAnswersModal: false,
    showMissingLinkedContactModal: false,
    showExportPresentationModal: false,
    markupTool: MARKUP_TOOLS.HIGHLIGHT,
    sorting: [],
    queries: {
      portfolios: '',
      contactPortfolios: '',
    },
    loading: {
      portfolios: false,
    },
    pendingPortfolios: [],
    showSuitabilityModal: false,
    suitabilityAnalysisModalDisplayMode: SuitabilityAnalysisModalDisplayMode.HIDDEN,
    proposalExportRequirementsDisplayMode: [ProposalExportRequirementsModalDisplayMode.HIDDEN],
    groupToEdit: undefined,
    showESGNoDataModal: false,
    redirectPath: NAV_PATHS.COMPARISONS,
    showCloseComparisonModal: false,
    comparisonPath: NAV_PATHS.COMPARISON,
    isExporting: false,
    showPortfolioUploadModal: false,
    showPortfolioAiStatementReaderModal: false,
    showMixedCurrencyModal: false,
    isConvertingMixedCurrency: false,
    portfolioNamesOutsideRiskProfileRange: [],
    timeSpendUsingHS: 0,
    timeStartUsingHS: 0,
  };

  isLinkMode = this.props.user.appConfig.mode === AppMode.LINK;

  fetchPortfoliosResults = debounce(() => {
    const { enterprise, user } = this.props;
    const { queries, sorting } = this.state;

    const promises: Promise<Portfolio[] | PaginatedPortfolios>[] = [];

    if (userHasPermissions(user, [PERMISSIONS.ALL_MODELS_LIST, PERMISSIONS.USER_MODELS, PERMISSIONS.ENTERPRISE_MODELS_LIST])) {
      // All models
      promises.push(this.props.getModelsPortfolios(undefined, queries.portfolios, sorting));
    }

    if (userHasPermission(user, PERMISSIONS.USER_MODELS)) {
      // My Models
      promises.push(this.props.getModelsPortfolios(modelType.User, queries.portfolios, sorting));
    }

    if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST) && enterprise) {
      // Enterprise Models
      promises.push(this.props.getModelsPortfolios(modelType.Enterprise, queries.portfolios, sorting));
    }

    Promise.all(promises).then(() => {
      this.setState(prevState => ({
        ...prevState,
        loading: {
          ...prevState.loading,
          portfolios: false,
        },
      }));
    });
  }, 300);

  fetchContactPortfoliosResults = debounce((sort?: SortingField[], shouldFetchNext?: boolean) => {
    const { queries } = this.state;

    this.props.getContactPortfolios(queries.contactPortfolios, sort, shouldFetchNext);
  }, 300);

  constructor(props: ComparisonBuilderProps) {
    super(props);

    this.state.comparisonPath = this.getStartingPath(props);
  }

  componentDidMount() {
    const { editedPortfolio, comparison, location, history, enterprise, user, contacts, isPostMessageActive } = this.props;
    const {
      'comparison-token': comparisonToken,
      portfolioId,
      contactId,
    } = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    }) as Dictionary<string>;
    const [, , secondPathSegment] = location.pathname.split('/');

    // Factset case : the user arrived directly by the ComparisonBuilder and do not have the contacts loaded yet
    const isPostMessageUserWithoutContacts = isPostMessageActive && !contacts.length;
    if (isPostMessageUserWithoutContacts && !userHasPermission(user, PERMISSIONS.COMPARISONS_LOAD_FACTSET_PORTFOLIOS)) {
      this.props.sendPostMessageGetContactsAndPortfolios();
    }

    // Shared comparison case :
    if (comparisonToken) {
      this.props.skipEmptyState();
      this.props.validateComparisonToken(comparisonToken).then(id => {
        heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_EXISTING_COMPARISON);
        this.props
          .getComparisonById(id)
          .then(dbComparison => {
            this.setState({
              comparisonPath: generatePath(NAV_PATHS.COMPARISON_DETAILS, {
                comparisonId: id,
              }),
            });
            this.props.loadComparison(dbComparison).then(res => {
              const path = this.generateComparisonPath(id);
              history.replace(`${path}`);
            });
          })
          .catch(err => {
            this.setState({ redirect: NAV_PATHS.COMPARISONS });
          });
      });
    }

    // Handle the second segment of the path: whether it is a specific comparison builder tab or an existing comparison id
    if (secondPathSegment) {
      const isComparisonTab = this.isComparisonNavPath(`/${secondPathSegment}`);
      const isNewComparison = secondPathSegment.startsWith('new-');
      const hasAlreadyLoadedThisComparison = comparison._id && comparison._id === secondPathSegment;
      if (isComparisonTab || isNewComparison) {
        this.loadComparisonFromStore();
      } else if (!hasAlreadyLoadedThisComparison) {
        this.loadComparisonFromParams(secondPathSegment);
      }
    }

    // This is the Salesforce Demo path. Route is /comparison?portfolioId={id}&contactId={id}
    if (portfolioId && contactId) {
      this.loadContactPortfolioFromParam(portfolioId, contactId);
    }

    this.props.setOriginPath(location.state);
    this.props.resetSearch();

    if (userHasPermission(user, PERMISSIONS.USER_MODELS)) {
      this.props.getModelsPortfolios(modelType.User);
    }

    this.props.getKycQuestionnaires();
    this.props.getQuestionnaires(QUESTIONNAIRE_TYPES.RATIONALE);

    if (comparison.rationaleAnswersId) {
      this.props.getQuestionnaireAnswersById(QUESTIONNAIRE_TYPES.RATIONALE, comparison.rationaleAnswersId);
    }

    this.props.loadProposalTemplates();

    // TODO move to the LoadPortfolioModal component
    if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST) && enterprise) {
      this.props.getModelsPortfolios(modelType.Enterprise);
    }

    if (editedPortfolio._id) {
      this.focusPortfolioEdit();
    }

    if (comparison.selectedCategories) {
      this.checkMissingSelectedCategories();
    }

    if (this.hasMissingLinkedContacts()) {
      this.setState({ showMissingLinkedContactModal: true });
    }

    if (this.isHistoricalScenariosTab() && !this.historicalScenariosIsHidden() && !this.state.timeStartUsingHS) {
      this.setState({ timeStartUsingHS: Date.now() });
    }

    // BMO is handled separately
    switch (true) {
      case location.pathname.includes(COMPARISON_NAV_PATHS.INCOME) && !this.incomeStreamIsHidden():
      case location.pathname.includes(COMPARISON_NAV_PATHS.RISK) && !this.riskProfileIsHidden(true):
      case location.pathname.includes(COMPARISON_NAV_PATHS.ESG) && userHasPermission(user, PERMISSIONS.ESG):
      case location.pathname.includes(COMPARISON_NAV_PATHS.MARKUP) && !this.markupIsHidden():
      case location.pathname.includes(COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS) && !this.historicalScenariosIsHidden():
      case location.pathname.includes(COMPARISON_NAV_PATHS.TAX_ANALYSIS) && !this.taxAnalysisIsHidden():
        history.replace(`${location.pathname}${location.search}`);
        break;
    }
  }

  getOmniRiskProfilesQuestionnaire = async (formId: UUID) => {
    try {
      await this.props.loadOmniRiskProfilesQuestionnaire(formId);
    } catch (e) {
      notification.error(formatMessage('OmniRiskProfilesQuestionnaireError'));
    }
  };

  loadContactPortfolioFromParam = async (portfolioId: string, contactId: string) => {
    // This is the Salesforce Demo path. Route is /comparison?portfolioId={id}&contactId={id}
    // Maybe /comparison/portfolio/:portfolioId/contactId={id}/performance?contact={id} would
    // be more standard, I really do not care.

    try {
      this.props.skipEmptyState();
      const portfolio = await this.props.getPortfolioById(portfolioId, PORTFOLIO_TYPES.CLIENT, contactId);
      if (portfolio != null) {
        await this.props.addPortfoliosToComparison([portfolio]);
        const contact = await this.props.getContactById(contactId);
        if (contact != null) {
          this.props.setComparisonContact(contact);
        }
        if (portfolio.riskProfile != null) {
          let questionnaires = this.props.kycQuestionnaires;
          if (questionnaires == null) {
            await this.props.getKycQuestionnaires();
          }
          questionnaires = this.props.kycQuestionnaires;
          for (const questionnaire of questionnaires) {
            for (const riskProfile of questionnaire.riskProfiles) {
              if (riskProfile._id === portfolio.riskProfile?.profile._id) {
                this.props.attachRiskProfile(riskProfile);
              }
            }
          }
        }
      }
    } finally {
      if (domUtils.appLoaderIsVisible()) {
        domUtils.removeAppLoader();
      }
    }
  };

  hasMissingLinkedContacts = (): boolean => {
    const { comparison, currentContact, user, contacts } = this.props;

    // Only applies to IG for now, second condition should never happen in this case
    if (user.appConfig.mode !== AppMode.CONTACT || !currentContact) {
      return false;
    }

    // Check if comparison items with contacts are from any of the contacts loaded only
    const comparisonItemsWithUnavailableContacts = comparison.items.filter(item => item.contact && !contacts.some(contact => contact._id === item.contact));

    if (!comparisonItemsWithUnavailableContacts.length) {
      return false;
    }

    const comparisonItemsWithLinkedContacts = comparison.items.filter(item => item.contact && item.contact !== currentContact.contact._id);

    // If the comparison has no items with other contacts, then we don't care
    if (!comparisonItemsWithLinkedContacts.length) {
      return false;
    }

    // If there are no linked contacts at this stage, it means the comparison had linked contacts before and they are no longer linked
    if (!currentContact.contact.linkedContacts?.length) {
      return true;
    }

    const linkedContactIds = currentContact.contact.linkedContacts.map(contact => contact._id);

    const missingContactItems = comparison.items.filter(item => {
      if (item.contact && !linkedContactIds.includes(item.contact) && item.contact !== currentContact.contact._id) {
        return item;
      }
      return null;
    });

    // Cast the length of missing contact items to a boolean
    return !!missingContactItems.length;
  };

  componentDidUpdate(prevProps: ComparisonBuilderProps) {
    const { editedPortfolio, comparison, postMessagePortfolios, setComparisonContact, setCurrentContact } = this.props;
    const { comparisonPath } = this.state;
    const { items, selectedCategories } = comparison;

    if (editedPortfolio._id && prevProps.editedPortfolio._id !== editedPortfolio._id) {
      this.focusPortfolioEdit();

      // Refresh sticky headers
      setTimeout(() => {
        dom.fireResizeEvent();
      }, 100);
    }

    if (comparison._id !== prevProps.comparison._id) {
      const contactId = comparison.contact?._id ? `?contact=${comparison.contact?._id}` : '';
      const comparisonTab = this.getComparisonTab();
      const redirectTab = comparisonTab && this.isComparisonNavPath(`/${comparisonTab}`) ? `/${comparisonTab}` : COMPARISON_NAV_PATHS.PERFORMANCE;
      window.history.replaceState({}, '', `${comparisonPath}${redirectTab}${contactId}`);

      if (comparison.rationaleAnswersId) {
        this.props.getQuestionnaireAnswersById(QUESTIONNAIRE_TYPES.RATIONALE, comparison.rationaleAnswersId);
      }
    }

    if (selectedCategories && prevProps.comparison.items.length !== items.length && !items.length && !comparisonIsReadOnly(comparison)) {
      this.props.setSelectedCategories(undefined);
    }

    // If we have postMessagesPortfolios, this means we are in the context of a contact via post messages
    // We don't get the contact on the portfolio from post message, so we filter it from the book of business
    // and assign it to the current contact and comparison
    if (prevProps.editedPortfolio._id !== editedPortfolio._id) {
      if (postMessagePortfolios?.length > 0) {
        const postMessagePortfolio = postMessagePortfolios.find(c => c._id === editedPortfolio._id);
        const postMessageContact = postMessagePortfolio?.contact;

        if (postMessageContact) {
          setCurrentContact(postMessageContact);
          setComparisonContact(postMessageContact);
        }
      }
    }
  }

  // Helper to get the current section label for announcement
  getSectionAnnouncement() {
    const { tab } = this.props;
    switch (tab) {
      case COMPARISON_NAV_PATHS.PERFORMANCE:
        return formatMessage('Comparison.TabsPanelAnnouncement.Performance');
      case COMPARISON_NAV_PATHS.GIF:
        return formatMessage('Comparison.TabsPanelAnnouncement.BMOGIF');
      case COMPARISON_NAV_PATHS.INCOME:
        return formatMessage('Comparison.TabsPanelAnnouncement.IncomeStream');
      case COMPARISON_NAV_PATHS.RISK:
        return formatMessage('Comparison.TabsPanelAnnouncement.RiskProfile');
      case COMPARISON_NAV_PATHS.MARKUP:
        return formatMessage('Comparison.TabsPanelAnnouncement.Markup');
      case COMPARISON_NAV_PATHS.ESG:
        return formatMessage('Comparison.TabsPanelAnnouncement.ESG');
      case COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS:
        return formatMessage('Comparison.TabsPanelAnnouncement.HistoricalScenarios');
      case COMPARISON_NAV_PATHS.TAX_ANALYSIS:
        return formatMessage('Comparison.TabsPanelAnnouncement.TaxAnalysis');
      default:
        return '';
    }
  }

  getStartingPath = (props: ComparisonBuilderProps) => {
    const { comparison, location } = props;
    const [, , secondPathSegment] = location.pathname.split('/');
    const isComparisonTab = this.isComparisonNavPath(`/${secondPathSegment}`);

    if (secondPathSegment && !isComparisonTab) {
      const comparisonId = secondPathSegment;
      return this.generateComparisonPath(comparisonId);
    }

    if (comparison._id && comparison._id.startsWith('new-')) {
      return NAV_PATHS.COMPARISON;
    }

    return this.generateComparisonPath(comparison._id);
  };

  generateComparisonPath = (id?: string) =>
    generatePath(NAV_PATHS.COMPARISON_DETAILS, {
      comparisonId: id || '',
    });

  isComparisonNavPath = (input: string) => {
    const path = input.split('?')[0];
    return Object.values<string>(COMPARISON_NAV_PATHS).includes(path);
  };

  getComparisonTab = () => {
    const { location } = this.props;
    return location.pathname.split('/').pop()?.split('?')[0];
  };

  loadComparisonFromStore = () => {
    const { comparison, history, location, user } = this.props;
    if (location.pathname.includes(COMPARISON_NAV_PATHS.GIF)) {
      if (userHasPermission(user, PERMISSIONS.BMO_GIF)) {
        this.props.skipEmptyState();
        this.setTab(COMPARISON_NAV_PATHS.GIF);
      } else {
        location.pathname = this.state.comparisonPath;
        history.replace(`${location.pathname}${location.search}`);
      }
    } else if (!comparison.items.length) {
      this.props.closeComparison(comparison);
      location.pathname = NAV_PATHS.COMPARISON;
      history.replace(`${NAV_PATHS.COMPARISON}`);
    } else {
      this.props.skipEmptyState();
      history.replace(`${NAV_PATHS.COMPARISON}${COMPARISON_NAV_PATHS.PERFORMANCE}`);
    }
  };

  loadComparisonFromParams = (comparisonId: string) => {
    const { history, location, user } = this.props;
    const searchParams = new URLSearchParams(location.search);
    const contactId = searchParams.has('contact') ? { _id: searchParams.get('contact') || undefined } : undefined;

    // Handles new BMO GIF comparison
    if (location.pathname === `${NAV_PATHS.COMPARISON}${COMPARISON_NAV_PATHS.GIF}`) {
      return;
    }

    this.props.skipEmptyState();
    this.props
      .getComparisonById(comparisonId, contactId)
      .then(dbComparison => {
        if (dbComparison._id) {
          this.setState({
            comparisonPath: generatePath(NAV_PATHS.COMPARISON_DETAILS, {
              comparisonId: dbComparison._id,
            }),
          });
          this.props.loadComparison(dbComparison).then(comparison => {
            if (comparisonHasMissingOrModifiedItems(comparison)) {
              this.setState({
                showComparisonMissingItemsModal: true,
                comparisonMissingItems: comparison,
              });
              return;
            }

            if (location.pathname.includes(COMPARISON_NAV_PATHS.GIF)) {
              if (userHasPermission(user, PERMISSIONS.BMO_GIF)) {
                this.setTab(COMPARISON_NAV_PATHS.GIF);
              } else {
                location.pathname = this.state.comparisonPath;
                history.replace(`${location.pathname}${location.search}`);
              }
            }
          });
        }
      })
      .catch(err => {
        this.setState({ redirect: NAV_PATHS.COMPARISONS });
      });
  };

  calculateTimeInHS = (isHistoricalScenariosTab: boolean) => {
    this.setState(prevState => ({
      timeSpendUsingHS: prevState.timeStartUsingHS ? prevState.timeSpendUsingHS + (Date.now() - prevState.timeStartUsingHS) : prevState.timeSpendUsingHS,
      timeStartUsingHS: isHistoricalScenariosTab && !prevState.timeStartUsingHS ? Date.now() : 0,
    }));
  };

  onTabChange = (item: NavigationItem) => {
    const { comparison, history } = this.props;
    const { comparisonPath, timeStartUsingHS } = this.state;
    const value = item.value || '';
    const tabValue = value.replace(comparisonPath, '');
    const contactId = comparison.contact?._id && !comparison._id?.startsWith('new-') ? `?contact=${comparison.contact?._id}` : '';
    const tab = `${comparisonPath}${tabValue}${contactId}`;
    const isHistoricalScenariosTab = tabValue === COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS;

    history.replace(tab);
    this.props.setTab(tabValue);
    this.calculateTimeInHS(isHistoricalScenariosTab);
    this.cancelEditPortfolio(this.props.editedPortfolio);

    // Reset view to all items
    this.props.setComparisonItemsFilter([ComparisonItemsFilter.All]);
  };

  setTab = (tab: string, replace?: boolean) => {
    const { location } = this.props;
    const { comparisonPath } = this.state;
    const path = `${comparisonPath}${tab}${location.search}`;
    if (replace) {
      this.props.history.replace(path);
    } else {
      this.props.history.push(path);
    }

    this.props.setTab(tab);
  };

  resetRoute = () => {
    this.setTab('', true);
  };

  checkMissingSelectedCategories = () => {
    const { comparison, user } = this.props;

    if (!comparison.selectedCategories || !userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)) {
      return;
    }

    const selectedCategoryKeys = Object.keys(comparison.selectedCategories);
    const filteredCategories = this.getFilteredCategories(true);
    const newCategories = filteredCategories.filter(c => !selectedCategoryKeys.includes(c.key));

    if (!newCategories.length) {
      return;
    }

    const selectedCategories = { ...comparison.selectedCategories };

    for (const category of newCategories) {
      selectedCategories[category.key] = {
        fields: category.fields.map((f: { key: any }) => f.key),
      };
    }

    this.props.setSelectedCategories(selectedCategories);
  };

  onExpandPortfolio = (item: ComparisonItem, expand: boolean) => {
    this.props.setExpandPortfolioItem(item, expand);

    // Refresh sticky headers
    requestAnimationFrame(() => {
      dom.fireResizeEvent();
    });
  };

  focusPortfolioEdit = () => {
    const cardsContainer = document.getElementsByClassName('comparison-cards')[0] as HTMLElement;
    const target = document.getElementsByClassName('comparison-card editing-mode')[0] as HTMLElement;

    if (cardsContainer && target && cardsContainer.scrollTo) {
      setTimeout(() => {
        cardsContainer.scrollTo({
          left: target.offsetLeft - COMPARISON_CARD_WIDTH,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  onItemRemove = (item: ComparisonItem) => {
    const { comparison } = this.props;

    if (item.type === COMPARISON_ITEM_TYPES.GROUP) {
      return this.showDeleteGroupModal([item]);
    }

    // See if we're emptying any groups by removing this item
    const groupsWithItem = comparison.items.filter(
      compItem => compItem.type === COMPARISON_ITEM_TYPES.GROUP && compItem.groupItems?.some(groupItem => groupItem.itemId === item._id),
    );

    // Any group left empty after removing the item also needs to be removed.
    const groupsToRemove = groupsWithItem.filter(group => group.groupItems?.length === 1);

    // No group needs to be removed - simply remove the item and don't look any further.
    if (groupsToRemove.length === 0) {
      this.props.removeItem(item._id);
      return;
    }
    // Keep the updates in a callback - this gets executed after confirming the deletion
    // of the groups.
    this.setState({
      deleteGroupsCallback: () => {
        // Remove the item itself
        this.props.removeItem(item._id);
      },
    });

    return this.showDeleteGroupModal(groupsToRemove);
  };

  getFilteredCategories = (disableSelectedCategories?: boolean) => {
    const {
      user,
      comparison,
      comparison: { selectedCategories },
      locale,
      tabSelections,
    } = this.props;
    const { previewMarkup } = this.state;

    if (!user) {
      return [];
    }

    const usedTypes = comparison.items.reduce((types, item) => {
      types.add(item.type);

      if (item.isExpanded) {
        for (const holding of item.holdings) {
          types.add(holding.type);
        }
      }

      return types;
    }, new Set() as Set<string>);

    const hasMarkupPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);

    const filterBySelectedCategories =
      !disableSelectedCategories &&
      !!user &&
      ((!hasMarkupPermission && !!comparison.providedBy) || (hasMarkupPermission && this.isMarkupTab() && previewMarkup));

    const filteredCategories = getComparisonCategories(user, comparison, locale, tabSelections).filter(cat => {
      if (cat.hidden) {
        return false;
      }

      const hideIfMixed = cat.hideIfMixed && usedTypes.size > 1;
      const filterCategoryByUsedTypes = cat.itemTypes && !cat.itemTypes.some((type: string) => usedTypes.has(type));

      // If the comparison was shared we show the category if one of the fields in it was selected
      if (filterBySelectedCategories && selectedCategories) {
        const selectedFieldsInCategory = cat.fields.slice().filter((field: ComparisonField) => fieldIsSelected(field, cat, selectedCategories));

        return selectedFieldsInCategory.length > 0 && !hideIfMixed && !filterCategoryByUsedTypes;
      }

      // If there are multiple item types in the comparison, keep only the
      // categories that doesn't have hideIfMixed set.
      if (hideIfMixed) {
        return false;
      }

      // Keep global categories, or categories that applies for at least one
      // item in the comparison.
      return !filterCategoryByUsedTypes;
    });

    return filteredCategories.map(cat => ({
      ...cat,
      fields: cat.fields.filter(f => {
        const filterFieldByUsedTypes = !f.itemTypes || f.itemTypes.some((type: string) => usedTypes.has(type));

        // If the comparison was shared we filter the fields based on what was selected
        if (filterBySelectedCategories && selectedCategories && selectedCategories[cat.key]) {
          return fieldIsSelected(f, cat, selectedCategories) && filterFieldByUsedTypes;
        }

        return !f.hidden && filterFieldByUsedTypes;
      }),
    }));
  };

  /**
   * Get categories for accessible table with expanded subcategories included
   * This includes subcategories that are normally hidden but should be shown
   * for Alternative Investment, Fixed Income Metrics, and Equity Metrics
   */
  getExpandedCategoriesForAccessibleTable = () => {
    const { user, comparison, locale, tabSelections } = this.props;

    if (!user) {
      return [];
    }

    const usedTypes = comparison.items.reduce((types, item) => {
      types.add(item.type);

      if (item.isExpanded) {
        for (const holding of item.holdings) {
          types.add(holding.type);
        }
      }

      return types;
    }, new Set() as Set<string>);

    // Get all categories including normally hidden ones
    const allCategories = getComparisonCategories(user, comparison, locale, tabSelections);

    // Categories that should include their expanded subcategories in accessible table
    const expandedCategoryKeys = ['altMetrics', 'fixedIncomeMetrics', 'equityMetrics'];

    // Subcategories to include when their parent is expanded
    const subcategoryMap: Record<string, string[]> = {
      fixedIncomeMetrics: ['creditQuality', 'maturityAllocations', 'fixedIncomeStyle'],
      equityMetrics: ['equityStyleBreakdown'],
      altMetrics: ['feeInformation', 'altPerformance'], // Fee Information and Alternative Performance
    };

    const filteredCategories = allCategories.filter(cat => {
      // Always include main categories that aren't hidden
      if (!cat.hidden) {
        const hideIfMixed = cat.hideIfMixed && usedTypes.size > 1;
        const filterCategoryByUsedTypes = cat.itemTypes && !cat.itemTypes.some((type: string) => usedTypes.has(type));

        if (hideIfMixed || filterCategoryByUsedTypes) {
          return false;
        }

        return true;
      }

      // For hidden categories, include them if they are subcategories of expanded categories
      const isSubcategory = Object.values(subcategoryMap).some((subcats: string[]) => subcats.includes(cat.key));
      if (isSubcategory) {
        // Check if the parent category exists and is visible
        const parentKey = Object.keys(subcategoryMap).find((parent: string) => subcategoryMap[parent]?.includes(cat.key));

        if (parentKey && expandedCategoryKeys.includes(parentKey)) {
          // Check if this subcategory should be shown based on item types
          const filterCategoryByUsedTypes = cat.itemTypes && !cat.itemTypes.some((type: string) => usedTypes.has(type));
          return !filterCategoryByUsedTypes;
        }
      }

      return false;
    });

    return filteredCategories.map(cat => ({
      ...cat,
      fields: cat.fields.filter(f => {
        const filterFieldByUsedTypes = !f.itemTypes || f.itemTypes.some((type: string) => usedTypes.has(type));
        return !f.hidden && filterFieldByUsedTypes;
      }),
    }));
  };

  onResavePortfolio = (item: ComparisonItem, toContact?: boolean) => {
    this.props.getPortfolioById(item._id, item.portfolioType, item.contact, item.enterprise, undefined, item.source).then((portfolio: Portfolio) => {
      this.setState({
        showAssignPortfolioModal: true,
        portfolioToSave: portfolio,
        portfolioSaveToContact: toContact,
      });
    });
  };

  hideAssignPortfolioModal = () => {
    this.setState({
      showAssignPortfolioModal: false,
      portfolioToSave: undefined,
      portfolioSaveToContact: undefined,
    });
  };

  hideMissingLinkedContactModal = () => {
    this.setState({ showMissingLinkedContactModal: false });
  };

  onModifyPortfolio = (item: ComparisonItem, forceShowTaxDetails = false) => {
    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return;
    }

    this.props.startLoaderAction(PROCESSING_COMPARISON_ITEM_ACTION.type);
    this.props
      .getPortfolioById(item._id, item.portfolioType, item.contact, item.enterprise, undefined, item.source)
      .then((portfolio: Portfolio) => {
        this.props.loadPortfolioForEdit(portfolio, item, undefined, undefined, undefined, forceShowTaxDetails);
      })
      .finally(() => {
        this.props.stopLoaderAction(PROCESSING_COMPARISON_ITEM_ACTION.type);
      });
  };

  onViewPortfolio = (item: ComparisonItem) => {
    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return;
    }

    this.props.startLoaderAction(PROCESSING_COMPARISON_ITEM_ACTION.type);
    this.props
      .getPortfolioById(item._id, item.portfolioType, item.contact, item.enterprise, undefined, item.source)
      .then((portfolio: Portfolio) => {
        this.props.loadPortfolioForEdit(portfolio, item, false, false, true);
      })
      .finally(() => {
        this.props.stopLoaderAction(PROCESSING_COMPARISON_ITEM_ACTION.type);
      });
  };

  onCombineIntoPortfolio = () => {
    this.props.combineIntoPortfolio();
  };

  onAddGroup = () => {
    this.showAddGroupModal();
  };

  onManageAccountSets = () => {
    this.showManageAccountSetsModal();
  };

  onModifyGroup = (item: ComparisonItem) => {
    this.showModifyGroupModal(item);
  };

  onConfirmAddGroup = async (name: string, color: string, products: ComparisonGroupProduct[]) => {
    const { groupToEdit } = this.state;

    this.hideAddGroupModal();

    if (groupToEdit) {
      this.props.updateComparisonGroup(groupToEdit._id, name, products, color);
      return;
    }

    this.props.addGroupToComparison(name, products, color);
  };

  onAddItemToGroup = (item: ComparisonItem, group: ComparisonItemGroup, value: number, fee?: number) => {
    this.props.addItemToGroup(item._id, group.itemId, value, fee);
  };

  onRemoveItemFromGroup = (item: ComparisonItem, group: ComparisonItemGroup) => {
    const { rawComparison } = this.props;

    const groupItem = rawComparison.items.find(i => i._id === group.itemId);
    if (!groupItem) {
      return;
    }

    if (groupItem.groupItems?.length === 1) {
      return this.showDeleteGroupModal([groupItem]);
    }

    this.props.removeItemFromGroup(item._id, group.itemId);
  };

  onRemoveSelectedComparisonItems = () => {
    const { selectedComparisonItems } = this.props;

    const groups = selectedComparisonItems.filter(item => item.type === COMPARISON_ITEM_TYPES.GROUP);

    if (groups.length > 0) {
      const otherItems = selectedComparisonItems.filter(item => item.type !== COMPARISON_ITEM_TYPES.GROUP);

      this.setState({
        deleteGroupsCallback: () => {
          otherItems.forEach(item => this.props.removeItem(item._id));
        },
      });

      return this.showDeleteGroupModal(groups);
    }

    selectedComparisonItems.forEach(item => this.props.removeItem(item._id));
  };

  onClonePortfolio = (item: ComparisonItem) => {
    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return;
    }
    this.props.startLoaderAction(PROCESSING_COMPARISON_ITEM_ACTION.type);
    this.props
      .getPortfolioById(item._id, item.portfolioType, item.contact, item.enterprise, undefined, item.source)
      .then((portfolio: Portfolio) => {
        this.props.clonePortfolio(portfolio);
      })
      .finally(() => {
        this.props.stopLoaderAction(PROCESSING_COMPARISON_ITEM_ACTION.type);
      });
  };

  addCategoryBenchmark = (item: ComparisonItem) => {
    this.props.addCategoryBenchmark(item).catch(showError);
  };

  onCloneBenchmark = (item: ComparisonItem) => {
    if (item.type !== COMPARISON_ITEM_TYPES.BENCHMARK) {
      return;
    }

    this.props.getBenchmarkPortfolio(item).then((benchmarkPortfolio: BenchmarkPortfolio) => {
      this.props.cloneBenchmark(benchmarkPortfolio);
    });
  };

  onPinPortfolioEditor = () => {
    this.props.togglePortfolioEditor(true, true, true, this.props.isEditReadOnly);
  };

  cancelEditPortfolio = (portfolio?: Portfolio) => {
    const { rawComparison } = this.props;

    const portfolioAdded = portfolio ? rawComparison.items.find(item => item._id === portfolio._id) : null;

    if (portfolio && portfolio._id && portfolio._id.startsWith('new-') && portfolioAdded) {
      this.props.removeItem(portfolio._id);
    }

    this.props.resetPortfolio();
    this.hideEditPortfolioModal();
  };

  onSavePortfolio = (portfolio: Portfolio, contact?: GenericContact, reset = true): Promise<Portfolio> => {
    const { rawComparison } = this.props;

    // Find id of the item pre-save - will be used to replace the proper item.
    const comparisonItem = rawComparison.items.find(item => item._id === portfolio._id);
    const id = comparisonItem != null && comparisonItem.portfolioType === PORTFOLIO_TYPES.UNASSIGNED && contact != null ? undefined : portfolio._id;

    return this.props.savePortfolio({ ...portfolio, _id: id }, contact).then(async (newPortfolio: Portfolio) => {
      // Only reset portfolio if we're closing the editor
      // In multi-portfolio mode with more portfolios, keep the state for the carousel
      if (reset && this.props.shouldCloseEditor) {
        this.props.resetPortfolio();
      }

      const groups = rawComparison.items.filter(
        item => item.type === COMPARISON_ITEM_TYPES.GROUP && item.groupItems?.some(groupItem => groupItem.itemId === portfolio._id),
      );

      await Promise.all(
        groups.map(group => this.props.updateComparisonGroup(group._id, formatLocalizedString(group.name), group.groupItems || [], group.color)),
      );

      const { pendingPortfolios } = this.state;
      if (pendingPortfolios.length) {
        this.setState(
          {
            pendingPortfolios: pendingPortfolios.filter(p => p._id !== id).concat([newPortfolio]),
          },
          () => {
            this.hideEditPortfolioModal();
          },
        );
        return newPortfolio;
      }

      if (this.props.shouldCloseEditor) {
        this.hideEditPortfolioModal();
      }

      if (comparisonItem !== undefined) {
        return this.props.replacePortfolioInComparison(newPortfolio, comparisonItem._id, this.isIncomeTab()).then(() => newPortfolio);
      }

      return this.props.addPortfoliosToComparison([newPortfolio]).then(() => newPortfolio);
    });
  };

  handlePendingPortfolios = () => {
    const { user } = this.props;
    const { pendingPortfolios } = this.state;

    if (pendingPortfolios && pendingPortfolios.length) {
      const portfolioWithMissingHolding = pendingPortfolios.find(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p));

      if (portfolioWithMissingHolding) {
        pendingPortfolios.splice(pendingPortfolios.indexOf(portfolioWithMissingHolding), 1);
        const portfolioClone = clonePortfolioAsProxy(portfolioWithMissingHolding);

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          portfolioClone.allocationMode = HoldingAllocationMode.Units;
        }

        this.props.loadPortfolioForEdit(portfolioClone);
      } else {
        this.addPortfoliosToComparison(pendingPortfolios);
        this.setState({ pendingPortfolios: [] });
      }
    }
  };

  onConfirmAssignPortfolio = (portfolioId: string | undefined, portfolio: Portfolio, contact?: GenericContact) => {
    const { rawComparison } = this.props;

    // Find id of the item pre-save - will be used to replace the proper item.
    const comparisonItem = rawComparison.items.find(item => item._id === portfolioId);

    if (!comparisonItem) {
      return;
    }

    this.props.savePortfolio(portfolio, contact).then((newPortfolio: Portfolio) => {
      if (!contact) {
        // If contact is not set, that means we assigned a new model - consume a models slot.
        this.props.consumeLimit(RateLimitType.MODELS);
      }

      notification.success(formatMessage('AssignPortfolioModal.Success'));

      this.props.replacePortfolioInComparison(newPortfolio, comparisonItem._id);
      this.hideAssignPortfolioModal();
    });
  };

  onSaveBenchmark = (benchmarkPortfolio: BenchmarkPortfolio) =>
    this.props.saveBenchmark(benchmarkPortfolio).then(newBenchmark => {
      this.props.replaceBenchmarkInComparison(newBenchmark, benchmarkPortfolio._id);
    });

  onEditBenchmarkCancel = (benchmarkPortfolio: BenchmarkPortfolio) => {
    if (benchmarkPortfolio._id && benchmarkPortfolio._id.startsWith('new-')) {
      this.props.removeItem(benchmarkPortfolio._id);
    }
  };

  onModifyBenchmark = (item: ComparisonItem) => {
    if (item.type !== COMPARISON_ITEM_TYPES.BENCHMARK) {
      return;
    }

    this.props
      .getBenchmarkPortfolio(item)
      .then((benchmarkPortfolio: BenchmarkPortfolio) => {
        this.props.loadBenchmarkForEdit(benchmarkPortfolio, item);
      })
      .catch(showError);
  };

  getTemplateMissingRequirements = (template: ProposalNS.ProposalTemplateV2) => {
    const { comparison } = this.props;
    const formattedRequirements = ProposalTemplateUtils.getFormattedMissingRequirements(comparison, template);

    // Add callbacks
    return formattedRequirements.map(requirement => {
      switch (requirement.key) {
        case ProposalNS.TemplateRequirementKeys.RiskProfile: {
          return {
            ...requirement,
            callback: this.redirectToRiskTab,
          };
        }
        default:
          return requirement;
      }
    });
  };

  onCreateOmniProposal = async () => {
    const { comparison, locale, user } = this.props;

    try {
      this.setState({ isExporting: true });

      if (!this.historicalScenariosIsHidden()) {
        this.recordHistoricalScenariosUsage('Online.Label');
      }

      const currentComparison = comparison.isDirty ? await this.onSaveComparison() : comparison;

      if (!currentComparison || !currentComparison._id) {
        return;
      }

      let createOmniExportResult = null;
      const comparisonId = currentComparison._id;
      if (userHasPermissionEnabled(user, PERMISSIONS.OMNI_PROPOSAL_FLOW_REWORK)) {
        createOmniExportResult = await this.props.createOmniExport(comparisonId, currentComparison.contact?._id);
      }

      const params = new URLSearchParams({
        comparisonId,
        ...(createOmniExportResult && {
          exportId: createOmniExportResult.comparisonExportId,
        }),
        productDataProvider: 'CORRELATE',
      });

      const localeOmni = getOmniLocale(locale);

      const url = new URL(`${import.meta.env.REACT_APP_OMNI_URL}/${localeOmni}/builder/template-selector?${params}`);
      return window.location.replace(url);
    } catch (err) {
      notification.error(formatMessage('Comparison.Omni.ExportFail'));
    } finally {
      this.setState({ isExporting: false });
    }
  };

  onCreatePDFProposal = async () => {
    const {
      isPortfolioDirty,
      editedPortfolio: portfolio,
      editedComparisonItem,
      proposalTemplates,
      proposalTemplate,
      rationaleQuestionnaires,
      rationaleCompleted,
      user,
      comparison,
      suitabilityAnalysisCompleted,
    } = this.props;

    if (!user) {
      return;
    }

    if (!this.historicalScenariosIsHidden()) {
      this.recordHistoricalScenariosUsage('PDF.Label');
    }

    const proposalExportRequirements: ProposalExportRequirementsModalDisplayMode[] = [];

    const missingProposalExportRequirements = await validateRiskProfileRequirementsForExport(comparison, user, this.props.getItemHoldingsData);

    if (missingProposalExportRequirements.length > 0) {
      proposalExportRequirements.push(...missingProposalExportRequirements);
    }

    const { displayModes, portfolioNamesOutsideRange } = await getItemsOutsideRiskProfileRange(comparison, user);

    if (displayModes.length > 0) {
      proposalExportRequirements.push(...displayModes);
    }
    if (portfolioNamesOutsideRange.length > 0) {
      this.setState({
        portfolioNamesOutsideRiskProfileRange: portfolioNamesOutsideRange,
      });
    }

    const missingRQRequirementsMessage = validateRationaleQuestionnaireRequirements({
      comparison,
      user,
      rationaleQuestionnaires,
      rationaleCompleted,
    });
    if (missingRQRequirementsMessage.length > 0) {
      proposalExportRequirements.push(...missingRQRequirementsMessage);
    }

    if (userHasPermission(user, PERMISSIONS.MIXED_CURRENCY_WARNING) && comparison.showBaseCurrencies) {
      const mixedCurrencyItems = this.getMixedCurrencyItems(comparison.items);

      if (mixedCurrencyItems.length > 0) {
        return this.showMixedCurrencyModal();
      }
    }

    if (
      userHasPermissionEnabled(user, PERMISSIONS.SUITABILITY_ANALYSIS) &&
      userHasPermissionEnabled(user, PERMISSIONS.SUITABILITY_ANALYSIS_REQUIRED) &&
      comparison.items.some(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) &&
      !suitabilityAnalysisCompleted
    ) {
      this.toggleSuitabilityAnalysisModal(SuitabilityAnalysisModalDisplayMode.PROPOSAL);
      return;
    }

    if (this.isRiskTab() && editedComparisonItem) {
      const allocation = getTotalPortfolioAllocation(portfolio);

      if (!formatLocalizedString(portfolio.name) || !portfolio.size || portfolio.size <= 0 || allocation !== 100) {
        this.showDiscardPortfolioModal();
        return;
      }

      if (isPortfolioDirty || (portfolio._id && portfolio._id.startsWith('new-'))) {
        this.showSavePortfolioModal();
        return;
      }
    }

    // Requirements only apply to V2 proposals
    if (proposalTemplate && proposalTemplate.builderVersion === ProposalNS.ProposalBuilderVersion.V2) {
      const missingProposalRequirements = this.getTemplateMissingRequirements(proposalTemplate as ProposalNS.ProposalTemplateV2);

      if (missingProposalRequirements.length > 0) {
        // Current comparison doesn't fulfill template requirements - see if there's any other usable template.
        const firstUsableTemplate = proposalTemplates.find(template => {
          // Note: V1 check preserved for backward compatibility, but current system only loads V2 templates
          // @ts-expect-error: Preserved for future V1 template support
          if (template.builderVersion === ProposalNS.ProposalBuilderVersion.V1) {
            return true;
          }

          const requirements = this.getTemplateMissingRequirements(template);
          return requirements.length === 0;
        });

        // If no template has their requirements met, display the missing requirements for the currently selected template
        if (!firstUsableTemplate) {
          this.showProposalRequirementsModal(missingProposalRequirements);
          return;
        }

        // Otherwise, open the proposal builder with this template
        this.props.setProposalTemplate(firstUsableTemplate);
      }
    }

    // It the comparison has an omni risk profile, we need to save it before exporting the proposal
    if (userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE) && this.comparisonHasOmniRiskProfile(comparison)) {
      // comparisonHasOmniRiskProfile validates that the formId exists
      this.getOmniRiskProfilesQuestionnaire((comparison.riskProfile as StateOmniRiskProfileConfig)!.formId!);
    }

    if (proposalExportRequirements.length > 0) {
      this.toggleProposalExportRequirementsModal(proposalExportRequirements);
      return;
    }

    this.createProposal();
  };

  createProposal = () => {
    this.props.resetProposal();
    this.props.history.push(NAV_PATHS.PROPOSAL);
  };

  closeSavePortfolioModal = () => this.setState({ showSavePortfolioModal: false });

  showSavePortfolioModal = () => this.setState({ showSavePortfolioModal: true });

  closeDiscardPortfolioModal = () => this.setState({ showDiscardPortfolioModal: false });

  showDiscardPortfolioModal = () => this.setState({ showDiscardPortfolioModal: true });

  closeDiscardComparisonModal = () => this.setState({ showDiscardComparisonModal: false });

  showDiscardComparisonModal = () => this.setState({ showDiscardComparisonModal: true });

  closeLoadModelPortfoliosModal = () => this.setState({ showLoadModelPortfoliosModal: false });

  showLoadModelPortfoliosModal = () => {
    this.onSearchPortfolios('');
    this.setState({ showLoadModelPortfoliosModal: true });
  };

  closeCloseComparisonModal = () => this.setState({ showCloseComparisonModal: false });

  showCloseComparisonModal = () => this.setState({ showCloseComparisonModal: true });

  closeProposalRequirementsModal = () => {
    this.setState({
      showProposalRequirementsModal: false,
      proposalRequirements: undefined,
    });
  };

  showProposalRequirementsModal = (missingRequirements: ProposalNS.FormattedProposalTemplateRequirements) => {
    this.setState({
      showProposalRequirementsModal: true,
      proposalRequirements: missingRequirements,
    });
  };

  handleShowPortfolioUploadModal = () => {
    const { user } = this.props;
    if (canAccessAIStatementReader(user)) {
      this.setState({ showPortfolioAiStatementReaderModal: true });
    } else {
      this.setState({ showPortfolioUploadModal: true });
    }
  };

  handleClosePortfolioUploadModal = () => {
    this.setState({
      showPortfolioUploadModal: false,
      showPortfolioAiStatementReaderModal: false,
    });
  };

  showNoAssetClassModalWithTab = (tab: NoAssetClass) => {
    this.setState({ showNoAssetClassModal: true, noAssetClassModalTab: tab });
  };

  closeNoAssetClassModal = () => this.setState({ showNoAssetClassModal: false });

  showESGNoDataModal = () => this.setState({ showESGNoDataModal: true });

  closeESGNoDataModal = () => this.setState({ showESGNoDataModal: false });

  showMixedCurrencyModal = () => this.setState({ showMixedCurrencyModal: true });

  closeMixedCurrencyModal = () => this.setState({ showMixedCurrencyModal: false });

  toggleProposalExportRequirementsModal = (proposalExportRequirements: ProposalExportRequirementsModalDisplayMode[]) => {
    this.setState({
      proposalExportRequirementsDisplayMode: proposalExportRequirements,
    });
  };

  onConfirmSavePortfolio = () => {
    const { editedPortfolio } = this.props;
    this.onSavePortfolio(editedPortfolio)
      .then(() => {
        this.closeSavePortfolioModal();
        this.onCreatePDFProposal();
      })
      .catch(showError);
  };

  onDeclineSavePortfolio = () => {
    this.closeSavePortfolioModal();
    this.createProposal();
  };

  onConfirmDiscardPortfolio = () => {
    this.closeDiscardPortfolioModal();
    this.createProposal();
  };

  onConfirmProposalRequirements = () => {
    this.closeProposalRequirementsModal();
  };

  onConfirmOpenNoAssetClassModal = (tab: ComparisonNavigationTab) => {
    this.closeNoAssetClassModal();

    switch (tab) {
      case ComparisonNavigationTab.IncomeStream:
        this.redirectToIncomeTab();
        break;
      case ComparisonNavigationTab.HistoricalScenarios:
        this.redirectToHistoricalScenariosTab();
        break;
      case ComparisonNavigationTab.TaxAnalysis:
        this.redirectToTaxAnalysis();
        break;

      default:
        break;
    }
  };

  onConfirmOpenESG = () => {
    this.closeESGNoDataModal();
    this.redirectToESGTab();
  };

  getComparisonContact = async () => {
    const { comparison } = this.props;

    if (comparison.contact?._id == null) {
      return undefined;
    }

    return this.props.getContactById(comparison.contact._id);
  };

  onCreatePortfolio = async () => {
    const contact = await this.getComparisonContact();
    this.props.loadNewPortfolio(contact, true);
  };

  isRiskTab = () => this.props.location.pathname.includes(COMPARISON_NAV_PATHS.RISK);

  isIncomeTab = () => this.props.location.pathname.includes(COMPARISON_NAV_PATHS.INCOME);

  isHistoricalScenariosTab = () => this.props.location.pathname.includes(COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS);

  isTaxAnalysisTab = () => this.props.location.pathname.includes(COMPARISON_NAV_PATHS.TAX_ANALYSIS);

  isMarkupTab = () => this.props.location.pathname.includes(COMPARISON_NAV_PATHS.MARKUP);

  isESGTab = () => this.props.location.pathname.includes(COMPARISON_NAV_PATHS.ESG);

  createProposalIsDisabled = () => {
    const { comparison, proposalTemplates } = this.props;

    return proposalTemplates.length === 0 || !comparisonHasItems(comparison, true) || (!!this.props.editedPortfolio._id && !this.isRiskTab());
  };

  comparisonHasOmniRiskProfile = (comparison: Comparison) => {
    const { riskProfile } = comparison;
    if (!riskProfile) return false;
    const hasFormId = 'formId' in riskProfile;
    const isOmniRiskProfile = 'isOmniRiskProfile' in riskProfile && riskProfile.isOmniRiskProfile;
    const hasQuestionnaireId = 'questionnaireId' in riskProfile;
    return hasFormId && isOmniRiskProfile && !hasQuestionnaireId;
  };

  onSaveComparison = async (isNewlyAssigned?: boolean) => {
    const savedComparison = await this.props.saveComparison(isNewlyAssigned);

    if (savedComparison._id) {
      this.setState({
        comparisonPath: generatePath(NAV_PATHS.COMPARISON_DETAILS, {
          comparisonId: savedComparison._id,
        }),
      });
    }
    return savedComparison;
  };

  getComparisonTimeSinceSave = () => {
    const { comparison } = this.props;

    let label;

    if (!comparison.lastRunAt || comparison.isDirty) {
      label = formatMessage('Comparison.Unsaved');
    } else {
      label = formatMessage('Comparison.TimeSinceSave', {
        timeSince: timeSince(comparison.lastRunAt, 300000),
      });
    }

    return <span className="updated-at-label">{label}</span>;
  };

  onSaveFromShare = async () => {
    const { comparison } = this.props;

    if (comparison._id && !comparison._id.startsWith('new-')) {
      await this.onSaveComparison();
      return true;
    }

    return false;
  };

  onTableScrollX = (atStart: boolean) => {
    if (!this.comparisonTable.current) {
      return;
    }

    if (atStart) {
      this.comparisonTable.current.classList.remove('is-scrolled-x');
    } else {
      this.comparisonTable.current.classList.add('is-scrolled-x');
    }
  };

  onComparisonNameChange = (name: string) => {
    let trimmedName = name.trim();

    if (!trimmedName) {
      trimmedName = this.DEFAULT_COMPARISON_NAME;
    }

    this.props.setComparisonName(trimmedName);
  };

  onRerunCurrentComparison = async () => {
    const newComparison = await this.props.rerunComparison(this.props.comparison);
    if (comparisonHasMissingOrModifiedItems(newComparison)) {
      this.setState({
        showComparisonMissingItemsModal: true,
        comparisonMissingItems: newComparison,
      });

      return;
    }

    await this.props.loadComparison(newComparison);
  };

  removeMissingItemsAndRefreshComparison = async () => {
    if (!this.state.comparisonMissingItems) {
      return;
    }

    let newComparison = await this.props.removeMissingComparisonData(this.state.comparisonMissingItems);
    newComparison = await this.props.rerunComparison(newComparison);

    await this.props.loadComparison(newComparison);

    this.setState({
      showComparisonMissingItemsModal: false,
      comparisonMissingItems: undefined,
    });
  };

  handleCancelMissingItemsModal = () => {
    this.setState({
      showComparisonMissingItemsModal: false,
      comparisonMissingItems: undefined,
    });
  };

  recordHistoricalScenariosUsage = (i18nKey: string) => {
    const {
      comparison: { selectedHistoricalScenarios },
      user,
    } = this.props;
    const { timeSpendUsingHS, timeStartUsingHS } = this.state;
    const totalTimeUsingHS = this.isHistoricalScenariosTab() ? timeSpendUsingHS + (Date.now() - timeStartUsingHS) : timeSpendUsingHS;
    const i18nBasePath = userHasPermissionEnabled(user, PERMISSIONS.OMNI_EXPORT_MODAL_REWORK) ? 'CreatePresentationModal' : 'ExportPresentationModal';
    const exportType = formatMessageWithLocale(`${i18nBasePath}.${i18nKey}`, LOCALE.EN_CA);
    logHistoricalScenariosUsage(totalTimeUsingHS, exportType, selectedHistoricalScenarios);
  };

  riskProfileIsHidden = (ignoreKyc?: boolean) => {
    const { user, comparison, kycQuestionnaires } = this.props;

    if (comparison.providedBy && comparison.riskProfile) {
      return false;
    }

    const readOnly = comparisonIsReadOnly(comparison);
    return (
      !user ||
      !userHasPermissionEnabled(user, PERMISSIONS.RISK_QUESTIONNAIRE, PERMISSIONS.RISK_PROFILE) ||
      (!ignoreKyc && kycQuestionnaires.length === 0) ||
      (readOnly && !comparison.riskProfile)
    );
  };

  markupIsHidden = () => {
    const { comparison, user } = this.props;

    const readOnly = comparisonIsReadOnly(comparison);
    const hasMarkup = comparison.items.some(i => !!i.markup);

    return !userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP) || (readOnly && !hasMarkup);
  };

  historicalScenariosIsHidden = () => {
    const { comparison, user } = this.props;

    const readOnly = comparisonIsReadOnly(comparison);
    const hasHistoricalScenarios: boolean = comparison.items.some((i: ComparisonItem) => !!i.historicalScenarios);

    return !userHasPermissionEnabled(user, PERMISSIONS.HISTORICAL_SCENARIOS) || (readOnly && !hasHistoricalScenarios);
  };

  taxAnalysisIsHidden = () => {
    const { user } = this.props;
    return !canAccessTaxAnalysis(user);
  };

  renderBMOGIFNavigationTab = () => {
    const { isLightVersion, locale } = this.props;
    const imgUrl = `https://capintel-uploads.s3.ca-central-1.amazonaws.com/enterprise-logos/2024/bmo-insurance-gif-white_${locale}.svg`;
    return (
      <div className="bmo-gif-tab">
        <img alt="BMO Insurance GIF Illustrator" src={imgUrl} className={classNames({ black: isLightVersion })} />
      </div>
    );
  };

  incomeStreamIsHidden = () => {
    const { user, comparison } = this.props;

    const readOnly = comparisonIsReadOnly(comparison);
    const hasScenarios = hasIncomeStreamScenarios(comparison.items);

    if (!comparison.providedBy && hasScenarios) {
      return false;
    }

    return !user || !userHasPermissionEnabled(user, PERMISSIONS.INCOME_STREAM) || comparisonIsCompliance(comparison) || (readOnly && !hasScenarios);
  };

  getNavigationTabs = () => {
    const { user, comparison } = this.props;
    const { comparisonPath } = this.state;

    const readOnly = comparisonIsReadOnly(comparison);

    const itemsWithStocksOrBonds = comparison.items.filter(i => doesItemHaveStocksOrBonds(i));
    const itemsWithAlts = comparison.items.filter(item => doesItemHasAlt(item));
    const itemsWithTypePortfolio = filterTaxAnalysisItems(comparison.items);
    const hasEsgComparisonItems = comparison.items.some(item => item.esg);
    const disableESG = readOnly && !hasEsgComparisonItems;

    const items: NavigationItem[] = [
      {
        label: formatMessage('Comparison.Tabs.Performance'),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.PERFORMANCE}`,
      },
      {
        element: this.renderBMOGIFNavigationTab(),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.GIF}`,
        hidden: !userHasPermission(user, PERMISSIONS.BMO_GIF),
        label: formatMessage('Comparison.Tabs.BMOGIF'),
      },
      {
        label: formatMessage('Comparison.Tabs.IncomeStream'),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.INCOME}`,
        hidden: this.incomeStreamIsHidden(),
        // TODO - When we fix stock data we can remove this.
        callback: itemsWithStocksOrBonds.length > 0 ? () => this.showNoAssetClassModalWithTab(ComparisonNavigationTab.IncomeStream) : undefined,
      },
      {
        label: formatMessage('Comparison.Tabs.RiskProfile'),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.RISK}`,
        hidden: this.riskProfileIsHidden(),
      },
      {
        label: formatMessage('Comparison.Tabs.Markup'),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.MARKUP}`,
        hidden: this.markupIsHidden(),
      },
      {
        label: formatMessage('Comparison.Tabs.ESG'),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.ESG}`,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.ESG),
        disabled: disableESG,
        tooltipMessage: disableESG ? 'ESGComparisonTool.RerunComparison' : undefined,
        callback: this.getExcludedItemsByESG(comparison.items).length ? this.showESGNoDataModal : undefined,
      },
      {
        label: formatMessage('Comparison.Tabs.HistoricalScenarios'),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS}`,
        hidden: this.historicalScenariosIsHidden(),
        callback: itemsWithAlts.length > 0 ? () => this.showNoAssetClassModalWithTab(ComparisonNavigationTab.HistoricalScenarios) : undefined,
      },
      {
        label: formatMessage('Comparison.Tabs.TaxAnalysis'),
        value: `${comparisonPath}${COMPARISON_NAV_PATHS.TAX_ANALYSIS}`,
        hidden: this.taxAnalysisIsHidden(),
      },
    ];

    return items.filter(i => !i.hidden);
  };

  getComparisonTable = (routeProps: RouteComponentProps, extraProps: Partial<ComparisonTableProps>, esgMode?: boolean) => {
    const { comparison, rawComparison, user, itemsFilter, locale } = this.props;
    const { duplicateFields, previewMarkup } = this.state;

    const readOnly = comparisonIsReadOnly(comparison);

    // As per requirements in CON-8699, Forward General Security holding should be filtered out
    // Since we only have access to the name of the holding, we use it to perform the filtering
    // "Forward General Security" is considered stable and will not change in the future
    let filteredItems = comparison.items.map(item => ({
      ...item,
      detailedHoldings: {
        ...item.detailedHoldings,
        list: item.detailedHoldings?.list?.filter(holding => holding.name !== 'Forward General Security') || [],
      },
    }));

    if (esgMode) {
      const excludedItemsByESG = this.getExcludedItemsByESG(filteredItems);
      filteredItems = filteredItems.filter(item => !excludedItemsByESG.find(excluded => item._id === excluded._id));
    }

    const categories = this.getFilteredCategories();

    return (
      <>
        {userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_METRICS_ACCESSIBLE_TABLE) && (
          <AccessibleComparisonTable
            comparison={comparison}
            items={filteredItems}
            categories={this.getExpandedCategoriesForAccessibleTable()}
            locale={locale}
            onModifyPortfolio={this.onModifyPortfolio}
            onViewPortfolio={this.onViewPortfolio}
            onClonePortfolio={this.onClonePortfolio}
            onModifyBenchmark={this.onModifyBenchmark}
            onCloneBenchmark={this.onCloneBenchmark}
            onItemRemove={this.onItemRemove}
            onSavePortfolio={this.onResavePortfolio}
            addCategoryBenchmark={this.addCategoryBenchmark}
            readOnly={readOnly}
          />
        )}

        <ComparisonTable
          {...routeProps}
          {...extraProps}
          user={user}
          comparison={comparison}
          rawComparison={rawComparison}
          itemsFilter={itemsFilter}
          items={filteredItems}
          readOnly={readOnly}
          onSavePortfolio={this.onResavePortfolio}
          onModifyPortfolio={this.onModifyPortfolio}
          onViewPortfolio={this.onViewPortfolio}
          addCategoryBenchmark={this.addCategoryBenchmark}
          onClonePortfolio={this.onClonePortfolio}
          onCloneBenchmark={this.onCloneBenchmark}
          onItemRemove={this.onItemRemove}
          categories={categories}
          onSaveBenchmark={this.onSaveBenchmark}
          onEditBenchmarkCancel={this.onEditBenchmarkCancel}
          setDuplicateFields={this.setDuplicateFields}
          duplicateFields={duplicateFields}
          resetMarkup={this.resetMarkup}
          updateSelectedCategories={this.props.updateSelectedCategories}
          onCellClick={this.onCellClick}
          onExpandPortfolioItem={this.onExpandPortfolio}
          previewMarkup={previewMarkup}
          togglePreviewMarkup={this.togglePreviewMarkup}
          onModifyBenchmark={this.onModifyBenchmark}
          onCombineIntoPortfolio={this.onCombineIntoPortfolio}
          onAddItemToGroup={this.onAddItemToGroup}
          onRemoveItemFromGroup={this.onRemoveItemFromGroup}
          onRemoveSelectedComparisonItems={this.onRemoveSelectedComparisonItems}
          popoverContainerRef={this.popoverContainerRef.current}
          onAddGroup={this.onAddGroup}
          onManageAccountSets={this.onManageAccountSets}
          onModifyGroup={this.onModifyGroup}
          onChangeComparisonItemsFilter={this.props.setComparisonItemsFilter}
          tabSelections={this.props.tabSelections}
          onTabSelectionChange={this.props.updateTabSelection}
        />
      </>
    );
  };

  toggleMarkupTool = (markupTool: string) => {
    this.setState(prevState => ({
      markupTool: prevState.markupTool !== markupTool ? markupTool : undefined,
    }));
  };

  togglePreviewMarkup = () => {
    this.setState(prevState => ({
      previewMarkup: !prevState.previewMarkup,
    }));
  };

  getDefaultSelectedCategories = (categories: ComparisonCategory[], duplicateFields?: string[]) => {
    let selectedCategories = {} as SelectedCategories;
    for (const category of categories) {
      const availableFields = getAvailableFields(category, duplicateFields);

      selectedCategories = Object.assign(selectedCategories, selectCategoryFields(category, availableFields));
    }

    return selectedCategories;
  };

  setDuplicateFields = (duplicateFields: string[]) => {
    this.setState({ duplicateFields });
  };

  resetMarkup = () => {
    const categories = this.getFilteredCategories();
    const duplicateFields = getDuplicateFields(categories);

    this.setDuplicateFields(duplicateFields);

    const selectedCategories = this.getDefaultSelectedCategories(categories, duplicateFields);
    this.props.setSelectedCategories(selectedCategories);
    this.props.clearAllMarkup();
  };

  onCellClick = (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) => {
    const {
      comparison: { selectedCategories },
    } = this.props;
    const { markupTool } = this.state;

    if ((!selectedCategories || !fieldIsSelected(field, category, selectedCategories)) && !markupTool) {
      return;
    }

    if (markupTool) {
      switch (markupTool) {
        case MARKUP_TOOLS.HIGHLIGHT: {
          if (get(item.markup, [`${category.key}`, `${field.key}`, 'highlight'])) {
            this.props.clearMarkup(item, category.key, field.key, MARKUP_TOOLS.HIGHLIGHT);
            return;
          }
          break;
        }
        case MARKUP_TOOLS.SQUARE: {
          if (get(item.markup, [`${category.key}`, `${field.key}`, 'shape']) === MARKUP_TOOLS.SQUARE) {
            this.props.clearMarkup(item, category.key, field.key, MARKUP_TOOLS.SQUARE);
            return;
          }
          break;
        }
        case MARKUP_TOOLS.CIRCLE: {
          if (get(item.markup, [`${category.key}`, `${field.key}`, 'shape']) === MARKUP_TOOLS.CIRCLE) {
            this.props.clearMarkup(item, category.key, field.key, MARKUP_TOOLS.CIRCLE);
            return;
          }
          break;
        }
      }

      this.props.applyMarkupToCell(item, category, field, markupTool);
    }
  };

  // Persist chart options changes across different modules via the redux store
  onChartOptionsChange = (options: ComparisonChartOptions, chart: PERFORMANCE_CHART) => {
    this.props.setChartOptions(options, chart);

    const { comparison } = this.props;
    const currentUseMonthly = comparison.options?.growth?.useMonthly;
    const newUseMonthly = (options as GrowthChartOptions).useMonthly;

    if (currentUseMonthly === true && newUseMonthly === false) {
      this.props.setComparisonShouldForceMonthlyData(false);
    } else if (newUseMonthly === undefined) {
      this.props.setComparisonShouldForceMonthlyData();
    }
  };

  getExpandedPortfoliosData = () => {
    const {
      comparison,
      comparison: { items },
      stockStore,
    } = this.props;
    const expandedItems = items.filter(i => i.isExpanded);

    const data: Dictionary<{
      funds: Dictionary<Fund>;
      stocks: Dictionary<Stock>;
    }> = {};

    expandedItems.forEach(item => {
      let fundsData = this.getFundsData(item.holdings);

      fundsData = item.holdings
        .filter(holding => holding.type === HOLDING_TYPES.CUSTOM_YIELD)
        .reduce(
          (acc, holding) => ({
            ...acc,
            [holding.id]: generateFundFromCustomYieldConfig(
              holding.yieldParams!,
              {
                filteredShelves: holding.filteredShelves,
                complianceComparisons: holding.complianceComparisons,
              },
              holding.currency,
              holding.id,
              comparison.monthEndDataOption,
            ),
          }),
          fundsData,
        );

      const stocksData = item.holdings
        .filter(h => h.type === HOLDING_TYPES.STOCK)
        .reduce((acc, h) => {
          let stock;
          switch (comparison.monthEndDataOption) {
            case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
              stock = stockStore.stocksRMED[h.id];
              break;
            case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
              stock = stockStore.stocksPMED[h.id];
              break;
            case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
              stock = stockStore.stocksRQED[h.id];
              break;
            case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
              stock = stockStore.stocksPQED[h.id];
              break;
            default:
              stock = stockStore.stocks[h.id];
              break;
          }

          return {
            ...acc,
            [h.id]: stock,
          };
        }, {} as Dictionary<Stock>);

      data[item._id] = {
        funds: fundsData,
        stocks: stocksData,
      };
    });

    return data;
  };

  getFundsData = (holdings: ComparisonHolding[]) => {
    const { comparison, fundStore } = this.props;
    return holdings
      .filter(holding => holding.type === HOLDING_TYPES.FUND)
      .reduce((acc, holding) => {
        let fund;
        switch (comparison.monthEndDataOption) {
          case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
            fund = fundStore.fundsRMED[holding.id];
            break;
          case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
            fund = fundStore.fundsPMED[holding.id];
            break;
          case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
            fund = fundStore.fundsRQED[holding.id];
            break;
          case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
            fund = fundStore.fundsPQED[holding.id];
            break;
          default:
            fund = fundStore.funds[holding.id];
            break;
        }
        return {
          ...acc,
          [holding.id]: fund,
        };
      }, {} as Dictionary<Fund>);
  };

  handleComparisonClose = () => {
    const { comparison, emptyStateIsSkipped, rawComparison } = this.props;

    this.setState({ redirectPath: NAV_PATHS.COMPARISONS }, () => {
      if (comparison.isDirty) {
        const hasItems = rawComparison.items.length > 0;

        if (!hasItems && !emptyStateIsSkipped) {
          return this.onCloseComparison();
        }

        return this.isLinkMode ? this.showCloseComparisonModal() : this.showDiscardComparisonModal();
      }

      this.onCloseComparison();
    });
  };

  onCloseComparison = () => {
    const { comparison, originPath } = this.props;
    const { redirectPath } = this.state;

    localStorage.clearProposalBrandingInfo();
    this.props.closeComparison(comparison);

    if (originPath) {
      return this.props.history.push(originPath.pathname);
    }

    this.props.history.push(redirectPath ?? NAV_PATHS.COMPARISONS);
  };

  onLoadMoreModelPortfolios = () => {
    const { queries, sorting } = this.state;
    const { nextModelsToken, user } = this.props;

    if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
      this.props.getModelsPortfolios(undefined, queries.portfolios, sorting, nextModelsToken);
    }
  };

  onLoadMoreMyModelsPortfolios = () => {
    const { queries, sorting } = this.state;
    const { nextMyModelsToken, user } = this.props;

    if (userHasPermission(user, PERMISSIONS.USER_MODELS)) {
      this.props.getModelsPortfolios(modelType.User, queries.portfolios, sorting, nextMyModelsToken);
    }
  };

  onLoadMoreEnterprisePortfolios = () => {
    const { queries, sorting } = this.state;
    const { nextEnterpriseModelsToken, user } = this.props;

    if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
      this.props.getModelsPortfolios(modelType.Enterprise, queries.portfolios, sorting, nextEnterpriseModelsToken);
    }
  };

  onLoadMoreContactPortfolios = (sort?: SortingField[]) => {
    const { queries } = this.state;

    if (!this.props.selectedContact) {
      return;
    }
    this.props.getContactPortfolios(queries.contactPortfolios, sort);
  };

  addPortfoliosToComparison = async (portfolioList: Portfolio[]) => {
    const { user, currentContact, getPortfolioById, rawComparison } = this.props;

    let portfolios;
    try {
      portfolios = await loadPortfoliosIfHoldingsAreMissing(portfolioList, getPortfolioById);
    } catch {
      return;
    }

    if (portfolios.some(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p))) {
      this.setState({ pendingPortfolios: portfolios }, () => this.handlePendingPortfolios());
    } else {
      const promises: Array<Promise<Portfolio>> = portfolios.map(
        portfolio =>
          new Promise(async resolve => {
            const inContactMode = user != null && user.appConfig.mode === AppMode.CONTACT && currentContact != null;
            const isModel = portfolio.mine;

            // Create a copy of the portfolio if it's either an enterprise or personal model
            if (inContactMode && portfolio.contact == null && (isEnterpriseModel(portfolio) || isModel)) {
              const newPortfolio = await this.props.saveModelCopyToDraftPortfolio(portfolio);
              resolve(newPortfolio);
            } else {
              resolve(portfolio);
            }
          }),
      );

      const localPortfolios = await Promise.all(promises);

      // Filter out portfolios that already exist in the comparison
      const newPortfolios = localPortfolios.filter(portfolio => !rawComparison.items.some(item => item._id === portfolio._id));

      if (newPortfolios.length === 0) {
        return; // No new portfolios to add
      }

      return this.props.addPortfoliosToComparison(newPortfolios).catch(err => {
        notification.errorFromKey(err.errorKey);
      });
    }
  };

  onSearchPortfolios = (query: string) => {
    this.setState(
      prevState => ({
        queries: {
          ...prevState.queries,
          portfolios: query,
        },
        loading: {
          ...prevState.loading,
          portfolios: true,
        },
      }),
      this.fetchPortfoliosResults,
    );
  };

  onSearchContactPortfolios = (query: string, sort?: SortingField[], shouldFetchNext?: boolean) => {
    this.props.initLoadingPortfolios();

    this.setState(
      prevState => ({
        queries: {
          ...prevState.queries,
          contactPortfolios: query,
        },
      }),
      () => this.fetchContactPortfoliosResults(sort, shouldFetchNext),
    );
  };

  redirectToRiskTab = () => {
    if (!this.isRiskTab()) {
      this.setTab(COMPARISON_NAV_PATHS.RISK);
    }
  };

  redirectToIncomeTab = () => {
    if (!this.isIncomeTab()) {
      this.setTab(COMPARISON_NAV_PATHS.INCOME);
    }
  };

  redirectToHistoricalScenariosTab = () => {
    if (!this.isHistoricalScenariosTab()) {
      this.setTab(COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS);
      this.calculateTimeInHS(true);
    }
  };

  redirectToESGTab = () => {
    if (!this.isESGTab()) {
      const selectedItem = this.getSelectedCardData();
      this.unselectComparisonItemsExceptFirst();

      if (selectedItem && !!this.getExcludedItemsByESG([selectedItem]).length) {
        this.props.unselectComparisonItem(selectedItem);
      }

      this.setTab(COMPARISON_NAV_PATHS.ESG);
    }
  };

  redirectToTaxAnalysis = () => {
    if (!this.isTaxAnalysisTab()) {
      const { comparison, unselectComparisonItem } = this.props;
      const selectedItem = this.getSelectedCardData();
      this.unselectComparisonItemsExceptFirst();

      if (selectedItem && !!filterTaxAnalysisItems(comparison.items).length) {
        unselectComparisonItem(selectedItem);
      }

      this.setTab(COMPARISON_NAV_PATHS.TAX_ANALYSIS);
    }
  };

  showEditPortfolioModal = () => {
    this.props.togglePortfolioEditor(true);
  };

  hideEditPortfolioModal = () => {
    this.props.togglePortfolioEditor(false);
  };

  handleDefaultRoute = () => {
    const { comparison } = this.props;
    const { comparisonPath } = this.state;
    const { items, gifItems } = comparison;
    const hasItems = items.length > 0;
    const hasGifItems = gifItems && gifItems.length > 0;
    const comparisonTab = this.getComparisonTab();
    const contactId = comparison.contact?._id && !comparison._id?.startsWith('new-') ? `?contact=${comparison.contact?._id}` : '';
    if (hasItems) {
      const redirectTab = comparisonTab && this.isComparisonNavPath(`/${comparisonTab}`) ? `/${comparisonTab}` : COMPARISON_NAV_PATHS.PERFORMANCE;
      return <Redirect to={`${comparisonPath}${redirectTab}${contactId}`} />;
    }
    if (hasGifItems) {
      return <Redirect to={`${comparisonPath}${COMPARISON_NAV_PATHS.GIF}${contactId}`} />;
    }

    heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
  };

  toggleSuitability = () => {
    this.setState(prevState => ({
      showSuitabilityModal: !prevState.showSuitabilityModal,
    }));
  };

  toggleDeleteSuitabilityAnswers = () => {
    this.setState(prevState => ({
      showDeleteRationaleAnswersModal: !prevState.showDeleteRationaleAnswersModal,
    }));
  };

  saveRationaleQuestionnaireAnswers = async (answers: QuestionnaireAnswers) => {
    const { comparison } = this.props;
    const isNewComparison = comparison._id && comparison._id.startsWith('new-');
    this.toggleSuitability();

    try {
      const questionnaire = getActiveQuestionnaire(this.props.rationaleQuestionnaires);
      const response = await this.props.saveQuestionnaireAnswers(
        QUESTIONNAIRE_TYPES.RATIONALE,
        answers,
        questionnaire,
        isNewComparison ? null : comparison._id!,
      );
      if (response._id && isNewComparison) {
        this.props.addRationaleAnswers(response._id);
      }

      notification.success(formatMessage('Comparison.Questionnaire.Rationale.SaveSuccess'));
    } catch (err) {
      notification.error(formatMessage('Comparison.Questionnaire.Rationale.SaveFail'));
    }
  };

  showDeleteRationaleAnswersModal = () => {
    const { rationaleAnswers } = this.props;

    if (rationaleAnswers?._id) {
      this.setState({ showSuitabilityModal: false }, () => {
        this.toggleDeleteSuitabilityAnswers();
      });
    }
  };

  toggleSuitabilityAnalysisModal = (displayMode: SuitabilityAnalysisModalDisplayMode) => {
    this.setState({ suitabilityAnalysisModalDisplayMode: displayMode });
  };

  deleteRationaleAnswers = () => {
    const { rationaleAnswers } = this.props;
    if (rationaleAnswers?._id) {
      this.props
        .deleteQuestionnaireAnswers(QUESTIONNAIRE_TYPES.RATIONALE, rationaleAnswers?._id)
        .then(() => {
          this.toggleDeleteSuitabilityAnswers();
          notification.success(formatMessage('Comparison.Questionnaire.Rationale.DeleteSuccess'));
        })
        .catch(() => {
          notification.error(formatMessage('Comparison.Questionnaire.Rationale.DeleteFail'));
        });
    }
  };

  showAddGroupModal = () => {
    this.setState({ showAddGroupModal: true });
  };

  hideAddGroupModal = () => {
    this.setState({
      showAddGroupModal: false,
      groupToEdit: undefined,
    });
  };

  showManageAccountSetsModal = () => {
    this.setState({ showManageAccountSetsModal: true });
  };

  hideManageAccountSetsModal = () => {
    this.setState({ showManageAccountSetsModal: false });
  };

  onSaveAccountSets = (accountSets: AccountSet[]) => {
    this.props.updateAccountSets(accountSets);
    this.hideManageAccountSetsModal();
  };

  showModifyGroupModal = (item: ComparisonItem) => {
    this.setState({
      groupToEdit: item,
      showAddGroupModal: true,
    });
  };

  onCancelAddGroup = () => {
    this.hideAddGroupModal();
  };

  editRationaleAnswers = async (questionnaireAnswers: QuestionnaireAnswers, questionnaire: Questionnaire) => {
    const { rationaleAnswers } = this.props;

    if (!rationaleAnswers?._id) {
      return;
    }

    this.toggleSuitability();

    try {
      await this.props.updateQuestionnaireAnswers(QUESTIONNAIRE_TYPES.RATIONALE, rationaleAnswers._id, questionnaireAnswers, questionnaire);
      notification.success(formatMessage('Comparison.Questionnaire.Rationale.SaveSuccess'));
    } catch (err) {
      notification.error(formatMessage('Comparison.Questionnaire.Rationale.SaveFail'));
    }
  };

  getTopBarLeftItems = () => {
    const { isEditVisible, isEditPinned, isLightVersion, isSaving, comparison, user, canPortfolioSave, isRerunning, isAddingToComparison } = this.props;

    const isCompliance = comparisonIsCompliance(comparison);
    const isReadOnly = comparisonIsReadOnly(comparison);
    const isEditable = comparisonIsEditable(comparison);
    const showRerun = isReadOnly && !isSaving && !isCompliance && isEditable;
    const showSave = user && !isCompliance && !isReadOnly && userHasPermissionEnabled(user, PERMISSIONS.COMPARISONS_SAVE);
    const canSave = showSave && !isEditVisible && !isEditPinned && !canPortfolioSave && !isAddingToComparison;

    const componentsArray: React.ReactNode[] = [];

    if (showRerun) {
      componentsArray.push(
        <Button
          onClick={this.onRerunCurrentComparison}
          dark={!isLightVersion}
          noBackground
          className="top-bar-button top-bar-button-extra-width"
          loading={isRerunning}
          icon="spin-arrow"
        >
          <FormattedMessage id={`Comparison.${isRerunning ? 'Rerunning' : 'RerunCurrent'}`} />
        </Button>,
      );
    }

    if (showSave && user) {
      componentsArray.push(
        <ComparisonSaveButton
          user={user}
          comparison={comparison}
          disabled={!canSave}
          onSavePortfolio={this.onSavePortfolio}
          onSaveComparison={this.onSaveComparison}
          onSetComparisonName={this.props.setComparisonName}
          onSetComparisonContact={this.props.setComparisonContact}
          onChangeItemId={this.props.changeItemId}
        />,
      );
    }

    return componentsArray;
  };

  createComparisonTopBarSettingModal = () => {
    const { user, comparison } = this.props;
    const chartOptions = this.props?.comparison?.options;

    const showShortHistory: boolean =
      userHasPermissionEnabled(user, PERMISSIONS.SHORT_HISTORY) &&
      comparison.items.some(item => ([COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.BENCHMARK] as string[]).includes(item.type));

    return (
      <ComparisonTopBarSettingsModal
        user={user}
        handleChartOptionsChange={this.onChartOptionsChange}
        capital={chartOptions?.growth?.capital}
        rebalancePeriodValue={chartOptions?.growth?.rebalancePeriod?.value}
        useMonthly={chartOptions?.growth?.useMonthly}
        showMonthlyRebalancingOption={userHasPermission(user, PERMISSIONS.USE_MONTHLY_REBALANCE)}
        useMonthlyRebalancing={chartOptions?.growth?.useMonthlyRebalancing}
        showBaseCurrencyOption={userHasPermission(user, PERMISSIONS.CURRENCY_CONVERSIONS)}
        baseCurrencyConversion={!!comparison.showBaseCurrencies}
        handleBaseCurrencyChange={this.props.showBaseCurrencies}
        showLongerHistoryOption={showShortHistory}
        showLongerHistory={comparison.shortHistory}
        handleLongerHistoryChange={this.props.toggleShortHistory}
        showMonthEndDataOption={!comparisonIsReadOnly(comparison) ? userHasPermissionEnabled(user, PERMISSIONS.MONTH_END_DATA) : false}
        autoMonthEndData={comparison.autoMonthEndData}
        monthEndDataOption={comparison.monthEndDataOption}
        handleMonthEndDataOptionChange={this.props.setMonthEndDataOption}
        endDate={chartOptions?.growth?.endDate}
        startDate={chartOptions?.growth?.startDate}
        locale={this.props.locale}
        isHistoricalScenariosTabActive={this.isHistoricalScenariosTab()}
      />
    );
  };

  getSettingsButton = () => {
    const { comparison, isLightVersion } = this.props;
    const readOnly: boolean = comparisonIsReadOnly(comparison);

    const showRerun = readOnly && !comparisonIsCompliance(comparison) && comparisonIsEditable(comparison);
    const tooltipText = showRerun ? formatMessage('Comparison.ReadOnlySettingsTooltip') : '';

    return (
      <ComparisonTopBarSettingsButton
        key="TopBarSettingsButton"
        tooltipText={tooltipText}
        disabled={readOnly}
        icon={isLightVersion ? 'settings1' : 'cog'}
        isLightVersion={isLightVersion}
        modal={this.createComparisonTopBarSettingModal()}
      />
    );
  };

  getTopBarMidRightItems = () => {
    const { user, comparison, isLightVersion, rationaleAnswers } = this.props;

    if (!user) {
      return [];
    }

    const componentsArray: React.ReactNode[] = [
      this.getSettingsButton(),
      ...(userHasPermission(user, PERMISSIONS.EXTERNAL_NOTIFICATION_CENTER)
        ? [
            <Button
              key="Comparison.NotificationCenter.Notifications"
              dark
              className={classNames('notification-center-trigger', 'top-bar-button')}
              icon={!isLightVersion ? 'notification-bell' : 'notification_bell_1'}
            >
              <FormattedMessage id="Comparison.NotificationCenter.Notifications" />
            </Button>,
          ]
        : []),
    ];

    if (this.props.comparison.contact !== undefined) {
      componentsArray.unshift(
        <ComparisonTopBarContactButton
          contact={this.props.comparison.contact}
          contactName={getComparisonContactName(this.props.comparison)}
          onClick={this.redirectToContactPage}
          isLightVersion={isLightVersion}
        />,
      );
    }

    const rationaleRequired = userHasPermission(user, PERMISSIONS.RATIONALE_REQUIRED);
    const hasRationale = userHasPermission(user, PERMISSIONS.RATIONALE_QUESTIONNAIRE) && this.props.rationaleQuestionnaires.length > 0;

    // If rationale is required, accessible or already filled out, display the button.
    const showRationale = rationaleRequired || hasRationale || rationaleAnswers;

    if (showRationale) {
      componentsArray.unshift(
        <Tooltip
          className={classNames({ 'custom-tooltip-v2': isLightVersion })}
          disabled={!rationaleAnswers}
          message={formatMessage('Comparison.Questionnaire.Rationale.IconToolTip')}
          place="bottom"
        >
          <Button onClick={this.toggleSuitability} dark noBackground className="top-bar-button">
            <i
              className={classNames('icon-bars', {
                'has-corner-checkmark': rationaleAnswers,
              })}
            />
            {rationaleAnswers && <i className={classNames('icon-checkmark', 'corner-checkmark')} />}
            <FormattedMessage id="Comparison.Questionnaire.Rationale.IconTitle" />
          </Button>
        </Tooltip>,
      );
    }

    const suitabilityAnalysisRequired = userHasPermission(user, PERMISSIONS.SUITABILITY_ANALYSIS);
    if (suitabilityAnalysisRequired) {
      const areSuitabilityAnalysisRequirementsMet = comparison.items.some(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
      componentsArray.unshift(
        <Tooltip
          className={classNames({ 'custom-tooltip-v2': isLightVersion })}
          disabled={areSuitabilityAnalysisRequirementsMet}
          message={formatMessage('Comparison.Questionnaire.SuitabilityAnalysis.IconToolTip')}
          place="left"
        >
          <span>
            <Button
              disabled={!areSuitabilityAnalysisRequirementsMet}
              onClick={() => this.toggleSuitabilityAnalysisModal(SuitabilityAnalysisModalDisplayMode.DEFAULT)}
              dark
              noBackground
              className="top-bar-button"
            >
              <i className="icon-circle-checkmark" />
              <FormattedMessage id="Comparison.Questionnaire.SuitabilityAnalysis.IconTitle" />
            </Button>
          </span>
        </Tooltip>,
      );
    }

    return componentsArray;
  };

  getMobileMenuItems = () => {
    const { user, comparison, isLightVersion, rationaleAnswers } = this.props;

    if (!user) {
      return [];
    }

    const readOnly: boolean = comparisonIsReadOnly(comparison);
    const componentsArray: React.ReactNode[] = [];

    // Add Status menu item if read-only
    if (readOnly) {
      componentsArray.push(<ComparisonTopBarStatusMenuItem key="status-menu-item" isLightVersion={isLightVersion} />);
    }

    // Add Contact menu item if contact exists
    if (comparison.contact !== undefined) {
      componentsArray.push(
        <ComparisonTopBarContactMenuItem
          key="contact-menu-item"
          contact={comparison.contact}
          contactName={getComparisonContactName(comparison)}
          onClick={this.redirectToContactPage}
        />,
      );
    }

    // Add Rationale Questionnaire button
    const rationaleRequired = userHasPermission(user, PERMISSIONS.RATIONALE_REQUIRED);
    const hasRationale = userHasPermission(user, PERMISSIONS.RATIONALE_QUESTIONNAIRE) && this.props.rationaleQuestionnaires.length > 0;
    const showRationale = rationaleRequired || hasRationale || rationaleAnswers;

    if (showRationale) {
      componentsArray.push(<ComparisonTopBarRationaleMenuItem key="rationale-menu-item" onClick={this.toggleSuitability} />);
    }

    // Add Suitability Analysis button
    const suitabilityAnalysisRequired = userHasPermission(user, PERMISSIONS.SUITABILITY_ANALYSIS);
    if (suitabilityAnalysisRequired) {
      const areSuitabilityAnalysisRequirementsMet = comparison.items.some(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
      componentsArray.push(
        <ComparisonTopBarSuitabilityAnalysisMenuItem
          key="suitability-analysis-menu-item"
          disabled={!areSuitabilityAnalysisRequirementsMet}
          onClick={() => this.toggleSuitabilityAnalysisModal(SuitabilityAnalysisModalDisplayMode.DEFAULT)}
        />,
      );
    }

    componentsArray.push(<ComparisonTopBarSettingsMenuItem key="settings-menu-item" disabled={readOnly} modal={this.createComparisonTopBarSettingModal()} />);

    return componentsArray;
  };

  redirectToContactPage = () => {
    const { comparison } = this.props;
    if (!comparison.contact || !comparison.contact._id) return;

    const contactPath = generatePath(NAV_PATHS.CONTACT_DETAILS, {
      contactId: comparison.contact._id || '',
    });

    this.setState({ redirectPath: contactPath }, () => {
      if (comparison.isDirty) {
        return this.showDiscardComparisonModal();
      }
      this.onCloseComparison();
    });
  };

  handleDeleteGroups = () => {
    const { itemsFilter, rawComparison } = this.props;
    const { groupItemsToDelete, deleteGroupsCallback } = this.state;

    if (!groupItemsToDelete || groupItemsToDelete.length === 0) {
      return;
    }

    groupItemsToDelete.forEach(item => {
      this.props.removeItem(item._id);
    });

    if (deleteGroupsCallback) {
      deleteGroupsCallback();
    }

    this.hideDeleteGroupModal();

    const isLastGroup = !rawComparison.items.some(
      item => item.type === COMPARISON_ITEM_TYPES.GROUP && !groupItemsToDelete.some(group => item._id === group._id),
    );

    // If we're deleting the last groups while being on the Groups tab, switch back to all items.
    if (itemsFilter.some(f => f === ComparisonItemsFilter.Groups) && isLastGroup) {
      this.props.setComparisonItemsFilter([ComparisonItemsFilter.All]);
    }
  };

  showDeleteGroupModal = (groupItemsToDelete: ComparisonItem[]) => {
    if (groupItemsToDelete.length === 0) {
      return;
    }

    this.setState({
      showDeleteGroupModal: true,
      groupItemsToDelete,
    });
  };

  hideDeleteGroupModal = () => {
    this.setState({
      showDeleteGroupModal: false,
      groupItemsToDelete: undefined,
      deleteGroupsCallback: undefined,
    });
  };

  onSortingChange = (sorting: SortingRule[]) => {
    this.setState({ sorting });
    this.fetchPortfoliosResults();
  };

  getExcludedItemsByESG = (items: ComparisonItem[] | any[]) =>
    items.filter(item => {
      if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP) {
        const fundsData = this.getFundsData(item.holdings);
        return Object.values(fundsData).every(
          item => item?.proxyRef?.model === 'ComparisonBenchmark' || item?.useAsBenchmark || item?._id.startsWith(BOND_TAG),
        );
      }
      return item.type === COMPARISON_ITEM_TYPES.BENCHMARK || item._id.startsWith(BOND_TAG);
    });

  selectComparedItems = (item: ComparisonItem, index = 0) => {
    const { selectedComparisonItems } = this.props;

    if (selectedComparisonItems.length > 1) {
      this.props.unselectComparisonItem(selectedComparisonItems[index]);
    }

    if (index === 1 && (selectedComparisonItems.length === 1 || selectedComparisonItems.length === 2)) {
      return this.props.selectComparisonItem(item);
    }

    if (selectedComparisonItems.length === 2) {
      return this.props.selectComparisonItem(item, false, 0);
    }

    this.props.selectComparisonItem(item, true);
  };

  getSelectedCardData = () => {
    const { selectedComparisonItems, comparison } = this.props;
    return selectedComparisonItems.length > 0 ? comparison.items.find(item => item._id === selectedComparisonItems[0]._id) : undefined;
  };

  handleScrollToCategory = (category: string) => {
    const { comparison } = this.props;
    const tableContainer = document.getElementsByClassName('comparison-table')[0] as HTMLElement;
    const categoryContainer = document.getElementsByClassName(`comparison-category ${category}`)[0] as HTMLElement;
    const stylesDifference = 208; // 172 (labels height) + 20 (labels margin) + 16 (prev. category margin)
    const metricsPosition = categoryContainer?.offsetTop + tableContainer?.offsetTop - stylesDifference;

    if (comparison.toggledCategories.indexOf(category) > -1) {
      window.scrollTo({ top: metricsPosition, behavior: 'smooth' });
      return;
    }

    const filteredCategories = this.getFilteredCategories();
    const hasCategory = filteredCategories.find(filteredCategory => filteredCategory.key === category);

    if (hasCategory) {
      this.props.toggleCategory(hasCategory);
      setTimeout(() => window.scrollTo({ top: metricsPosition, behavior: 'smooth' }));
    }
  };

  setScrollToTopPosition = (element: string) => {
    const tableContainer = document.getElementsByClassName(element)[0] as HTMLElement;
    if (!tableContainer) return;
    return tableContainer.offsetTop;
  };

  renderLinkHeader = () => {
    const { user } = this.props;
    return (
      <LinkHeader
        user={user}
        middleItems={<LinkLogo user={user} />}
        leftItems={<CloseButton key="ComparisonTopBarNavigation.CloseButton" dark withText onClick={this.handleComparisonClose} className="top-bar-button" />}
      />
    );
  };

  renderDefaultHeader = () => {
    const { comparison, isSaving, setComparisonName } = this.props;
    const readOnly = comparisonIsReadOnly(comparison);

    return (
      <ComparisonTopBarNavigationResponsive
        comparison={comparison}
        isSaving={isSaving}
        setComparisonName={setComparisonName}
        readOnly={readOnly}
        getTopBarLeftItems={this.getTopBarLeftItems}
        getTopBarMidRightItems={this.getTopBarMidRightItems}
        getMobileMenuItems={this.getMobileMenuItems}
        handleComparisonClose={this.handleComparisonClose}
      />
    );
  };

  getMixedCurrencyItems = (items: ComparisonItem[]) =>
    items.filter(
      item =>
        (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP || item.type === COMPARISON_ITEM_TYPES.BENCHMARK) &&
        item.currency === CURRENCY.MIXED,
    );

  onConfirmMixedCurrency = async () => {
    const { comparison } = this.props;

    this.setState({ isConvertingMixedCurrency: true });
    await this.props.showBaseCurrencies(false);

    if (comparison._id && !comparisonIsReadOnly(comparison) && (comparison.contact || (!comparison.contact && !comparison._id.startsWith('new-')))) {
      await this.onSaveComparison();
    }

    this.setState({ isConvertingMixedCurrency: false });
    this.closeMixedCurrencyModal();
    this.onCreatePDFProposal();
  };

  onShowExportPresentationModal = () => {
    this.setState({ showExportPresentationModal: true });
  };

  unselectComparisonItemsExceptFirst = () => {
    const { selectedComparisonItems, unselectComparisonItem } = this.props;
    selectedComparisonItems.slice(1).forEach(item => unselectComparisonItem(item));
  };

  render() {
    const {
      comparison,
      user,
      nextModelsToken,
      nextMyModelsToken,
      nextEnterpriseModelsToken,
      selectedContact,
      locale,
      rationaleQuestionnaires,
      enterprise,
      modelPortfolios,
      myModelsPortfolios,
      enterpriseModelsPortfolios,
      emptyStateIsSkipped,
      location,
      currentContact,
      isEditVisible,
      isEditPinned,
      editedPortfolio,
      editedScenario,
      editedComparisonItem,
      isComparingPortfolios,
      rawComparison,
      isAddingToComparison,
      isLightVersion,
      userPreferences,
      comparisonIsLoading,
      isProcessing,
      presentationTemplates,
      openLoadClientPortfoliosModal,
      shareComparison,
      isFFEnabled,
    } = this.props;

    const {
      redirect,
      markupTool,
      queries,
      loading,
      showSavePortfolioModal,
      showDiscardPortfolioModal,
      previewMarkup,
      showDiscardComparisonModal,
      showLoadModelPortfoliosModal,
      showProposalRequirementsModal,
      proposalRequirements,
      showAssignPortfolioModal,
      proposalExportRequirementsDisplayMode,
      portfolioToSave,
      portfolioSaveToContact,
      showNoAssetClassModal,
      showSuitabilityModal,
      showDeleteRationaleAnswersModal,
      suitabilityAnalysisModalDisplayMode,
      showDeleteGroupModal,
      groupItemsToDelete,
      showAddGroupModal,
      showManageAccountSetsModal,
      showComparisonMissingItemsModal,
      groupToEdit,
      showESGNoDataModal,
      comparisonMissingItems,
      showMissingLinkedContactModal,
      showCloseComparisonModal,
      comparisonPath,
      showPortfolioUploadModal,
      showPortfolioAiStatementReaderModal,
      showMixedCurrencyModal,
      isConvertingMixedCurrency,
      portfolioNamesOutsideRiskProfileRange,
      showExportPresentationModal,
    } = this.state;

    if (!user) {
      return null;
    }

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    const { items } = comparison;
    const readOnly = comparisonIsReadOnly(comparison);
    const filteredItems = getFilteredItems(items);
    const hasItems = rawComparison.items.length > 0;
    const showQuickSearch = !readOnly && !location.pathname.includes('bmo-gif');
    const currentRationaleQuestionnaire = getActiveQuestionnaire(rationaleQuestionnaires);

    const hasGroupsPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_GROUPS);
    const contact = currentContact != null ? currentContact.contact : undefined;
    const showMarkupRoute = hasItems && !!user && userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);

    const hasSharedGroups = !!comparison.providedBy && rawComparison.items.some(item => item.type === COMPARISON_ITEM_TYPES.GROUP);
    const showGroups = hasSharedGroups || hasGroupsPermission;
    const showAccountSets = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_ACCOUNT_SETS) && isFFEnabled(FEATURE_FLAGS.ACCOUNT_SETS_ENABLED);
    const editGroups = !readOnly && hasGroupsPermission;
    const taxAnalysisComparisonItems = filteredItems.filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);

    const excludedItemsByESG = this.getExcludedItemsByESG(items);

    const esgComparisonItems = filteredItems.filter(item => !excludedItemsByESG.find(excluded => item._id === excluded._id));
    const itemsWithStocksOrBonds = items.filter(i => doesItemHaveStocksOrBonds(i));
    const itemsWithAlts = items.filter(item => doesItemHasAlt(item));
    const itemsWithTypePortfolio = filterTaxAnalysisItems(items);

    const itemsByTab = {
      'income-stream': itemsWithStocksOrBonds,
      'historical-scenarios': itemsWithAlts,
      'tax-analysis': itemsWithTypePortfolio,
    };

    if (!hasItems && !emptyStateIsSkipped) {
      return (
        <EmptyComparison
          isComparingPortfolios={isComparingPortfolios}
          user={user}
          queries={queries}
          selectedContact={selectedContact}
          resetRoute={this.resetRoute}
          savePortfolio={this.props.savePortfolio}
          onCloseComparison={this.handleComparisonClose}
          addPortfoliosToComparison={this.addPortfoliosToComparison}
          loadContactPortfolios={this.props.loadContactPortfolios}
          onSearchContactPortfolios={this.onSearchContactPortfolios}
          onLoadMoreContactPortfolios={this.onLoadMoreContactPortfolios}
          resetCurrentContact={this.props.resetCurrentContact}
          resetPortfolio={this.props.resetPortfolio}
          getComparisonContact={this.getComparisonContact}
          setPortfolioContact={this.props.setPortfolioContact}
          loadNewPortfolio={this.props.loadNewPortfolio}
          loadPortfolioForEdit={this.props.loadPortfolioForEdit}
          locale={this.props.locale}
        />
      );
    }

    const growthChartView = (props: RouteComponentProps) => (
      <>
        {this.isLinkMode && (
          <div className="comparison-tab-performance-settings">
            <ComparisonTopBarSettingsButton key="TopBarSettingsButton" modal={this.createComparisonTopBarSettingModal()} isLightVersion={isLightVersion} />
          </div>
        )}
        <ChartContainer
          {...props}
          readOnly={readOnly}
          locale={locale}
          user={user}
          key={comparison._id}
          items={filteredItems}
          onChartOptionsChange={this.onChartOptionsChange}
          comparison={comparison}
          expandedPortfoliosData={this.getExpandedPortfoliosData()}
          isAddingToChart={isAddingToComparison}
          dark={!isLightVersion}
        />
      </>
    );

    const riskProfileView = (props: RouteComponentProps) => {
      if (this.props.kycQuestionnaires.length === 0) {
        return null;
      }

      return (
        <RiskProfileView
          {...props}
          comparison={comparison}
          riskProfile={comparison.riskProfile}
          onSavePortfolio={this.onSavePortfolio}
          readOnly={readOnly}
          dark={!isLightVersion}
        />
      );
    };

    const incomeStreamView = () => (
      <div className="chart-container no-sidebar">
        <IncomeChart
          readOnly={readOnly}
          dark={!isLightVersion}
          comparison={comparison}
          items={filterIncomeStreamItems(filteredItems)}
          options={comparison.options && comparison.options.income}
          onOptionsChange={(chartOptions: IncomeChartOptions) => this.onChartOptionsChange(chartOptions, PERFORMANCE_CHART.INCOME)}
          title={formatMessage('Charts.Income.Title')}
          showDateRange={!readOnly}
          shortHistory={comparison.shortHistory}
          editedScenario={editedScenario}
          editedComparisonItem={editedComparisonItem}
          calculateIncomeStream={this.props.calculateIncomeStream}
          isAddingToChart={isAddingToComparison}
          showBaseCurrencies={comparison.showBaseCurrencies}
        />
      </div>
    );

    const bmoGifView = (props: RouteComponentProps) => <BMOGIFView {...props} comparison={comparison} readOnly={readOnly} />;

    const markupToolbarView = () => (
      <MarkupToolbar
        selectedMarkupTool={markupTool}
        toggleMarkupTool={this.toggleMarkupTool}
        resetMarkup={this.resetMarkup}
        hidden={previewMarkup || readOnly}
      />
    );

    const esgView = () => (
      <ESGView
        availableComparisonItems={esgComparisonItems}
        selectedItems={this.props.selectedComparisonItems.slice(0, 2)}
        onRemoveItem={this.props.unselectComparisonItem}
        onSelectItem={(item, index) => this.selectComparedItems(item, index)}
        onScrollToMetrics={() => this.handleScrollToCategory(COMPARISON_CATEGORIES.ESG_OVERVIEW)}
        locale={locale}
        theme={userPreferences?.theme || ThemeMode.DEFAULT}
      />
    );

    const HistoricalScenariosView = () => <HistoricalScenarios readOnly={readOnly} />;

    const TaxAnalysisViewTabContent = () => (
      <TaxAnalysisView
        availableComparisonItems={taxAnalysisComparisonItems}
        onEditItem={this.onModifyPortfolio}
        onCreatePortfolio={this.onCreatePortfolio}
        readOnly={readOnly}
      />
    );

    const isExportRequirementModalHidden = proposalExportRequirementsDisplayMode.includes(ProposalExportRequirementsModalDisplayMode.HIDDEN);

    const showPortfolioActions = !readOnly && !location.pathname.includes('markup') && !location.pathname.includes('bmo-gif');
    const onlyGifIllustration = !!comparison && !comparison.items.length && comparison.gifItems && comparison.gifItems.length === 1;
    const shareIsDisabled = (!!editedPortfolio._id && editedPortfolio._id.startsWith('new-')) || !!onlyGifIllustration;

    return (
      <>
        <DelegateBanner />
        <AnnouncementBanner />

        <div className="comparison-builder-navigation">{this.isLinkMode ? this.renderLinkHeader() : this.renderDefaultHeader()}</div>

        <div className="comparison-builder-graphs-container">
          <div
            className={classNames('comparison-header', {
              'is-light-version': isLightVersion,
            })}
          >
            <div className="d-flex comparison-header-content">
              <div className="left-container flex-fill">
                {showQuickSearch && (
                  <div className="comparison-search-container">
                    <ComparisonQuickSearch dark={!isLightVersion} isIncomeTab={this.isIncomeTab()} user={user} />
                  </div>
                )}
              </div>

              <div className="comparison-actions flex-column flex-grow-1">
                <ComparisonActionButtons
                  createProposalIsDisabled={this.createProposalIsDisabled()}
                  comparison={comparison}
                  isLightVersion={isLightVersion}
                  isExporting={this.state.isExporting}
                  onCreatePortfolio={this.onCreatePortfolio}
                  openLoadClientPortfoliosModal={openLoadClientPortfoliosModal}
                  onCreatePDFProposal={this.onCreatePDFProposal}
                  onSaveFromShare={this.onSaveFromShare}
                  onShowExportPresentationModal={this.onShowExportPresentationModal}
                  portfolioActionsDisabled={!!editedPortfolio._id}
                  shareComparison={shareComparison}
                  shareIsDisabled={shareIsDisabled}
                  showPortfolioUploadModal={this.handleShowPortfolioUploadModal}
                  showLoadModelPortfoliosModal={this.showLoadModelPortfoliosModal}
                  showPortfolioActions={showPortfolioActions}
                  user={user}
                />
              </div>
            </div>
          </div>

          {/* Aria-live region for announcing section changes */}
          <div aria-live="polite" aria-atomic="true" className="visually-hidden">
            {this.getSectionAnnouncement()}
          </div>
          <div className="comparison-tabs">
            <PrimaryNavigation className="d-flex align-items-center" dark={!isLightVersion} items={this.getNavigationTabs()} onChange={this.onTabChange} />
            <hr className="comparison-chart-navigation-separator" />
            <div
              className={classNames('comparison-tabs-content', {
                'markup-mode': this.isMarkupTab(),
              })}
            >
              <ProtectedView path={`${comparisonPath}(${COMPARISON_NAV_PATHS.PERFORMANCE}|${COMPARISON_NAV_PATHS.MARKUP})`} view={growthChartView} />

              <ProtectedView
                path={`${comparisonPath}${COMPARISON_NAV_PATHS.INCOME}`}
                view={incomeStreamView}
                permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.INCOME_STREAM)}
              />

              <ProtectedView
                path={`${comparisonPath}${COMPARISON_NAV_PATHS.GIF}`}
                view={bmoGifView}
                permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.BMO_GIF)}
              />

              <ProtectedView
                path={`${comparisonPath}${COMPARISON_NAV_PATHS.RISK}`}
                view={riskProfileView}
                permissionCallback={() => !this.riskProfileIsHidden()}
              />

              <ProtectedView
                path={`${comparisonPath}${COMPARISON_NAV_PATHS.MARKUP}`}
                view={markupToolbarView}
                permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)}
              />

              <ProtectedView
                path={`${comparisonPath}${COMPARISON_NAV_PATHS.ESG}`}
                view={esgView}
                permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.ESG)}
              />

              <ProtectedView
                path={`${comparisonPath}${COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS}`}
                view={HistoricalScenariosView}
                permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.HISTORICAL_SCENARIOS)}
              />

              <ProtectedView
                path={`${comparisonPath}${COMPARISON_NAV_PATHS.TAX_ANALYSIS}`}
                view={TaxAnalysisViewTabContent}
                permissionCallback={() => !this.taxAnalysisIsHidden()}
              />
            </div>
          </div>
        </div>

        <div className="position-relative">
          <div ref={this.popoverContainerRef} className="comparison-table-popover-container" />
          <Switch>
            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.PERFORMANCE}`}
              view={(props: RouteComponentProps) =>
                hasItems &&
                this.getComparisonTable(props, {
                  showGroups,
                  editGroups,
                  showAccountSets,
                })
              }
            />

            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.GIF}`}
              permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.BMO_GIF)}
              view={() => <BMOGIFComparisonTable comparison={comparison} readOnly={readOnly} />}
            />

            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.RISK}`}
              permissionCallback={() => !this.riskProfileIsHidden()}
              view={(props: RouteComponentProps) =>
                hasItems &&
                this.getComparisonTable(props, {
                  multiselect: false,
                  showActionBar: false,
                  showGroups: false,
                  showAccountSets: false,
                  allowItemSelection: true,
                })
              }
            />

            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.INCOME}`}
              permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.INCOME_STREAM)}
              view={(props: RouteComponentProps) => (
                <IncomeStreamTable
                  items={filteredItems}
                  comparison={comparison}
                  startDate={getIncomeStartDate(comparison)}
                  endDate={getIncomeEndDate(comparison)}
                  onSavePortfolio={this.onSavePortfolio}
                  readOnly={readOnly}
                  isEditPinned={isEditPinned}
                  onModifyPortfolio={this.onModifyPortfolio}
                  onClonePortfolio={this.onClonePortfolio}
                  onItemRemove={this.onItemRemove}
                  popoverContainerRef={this.popoverContainerRef.current}
                  {...props}
                />
              )}
            />

            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.MARKUP}`}
              permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)}
              view={(props: RouteComponentProps) =>
                showMarkupRoute &&
                this.getComparisonTable(props, {
                  selection: false,
                  markupMode: true,
                  showActionBar: false,
                  showGroups: false,
                  showAccountSets: false,
                  markupTool,
                })
              }
            />

            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.ESG}`}
              permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.ESG)}
              view={(props: RouteComponentProps) =>
                hasItems &&
                this.getComparisonTable(
                  props,
                  {
                    multiselect: true,
                    showActionBar: false,
                    showGroups: false,
                    showAccountSets: false,
                    allowItemSelection: true,
                  },
                  true,
                )
              }
            />

            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.HISTORICAL_SCENARIOS}`}
              permissionCallback={() => userHasPermissionEnabled(user, PERMISSIONS.HISTORICAL_SCENARIOS)}
              view={(props: RouteComponentProps) =>
                hasItems && (
                  <HistoricalScenariosTable
                    items={filteredItems}
                    comparison={comparison}
                    isEditPinned={isEditPinned}
                    popoverContainerRef={this.popoverContainerRef.current}
                    {...props}
                  />
                )
              }
            />

            <ProtectedView
              path={`${comparisonPath}${COMPARISON_NAV_PATHS.TAX_ANALYSIS}`}
              permissionCallback={() => !this.taxAnalysisIsHidden()}
              view={(props: RouteComponentProps) =>
                hasItems && (
                  <TaxAnalysisTable
                    items={filteredItems}
                    comparison={comparison}
                    isEditPinned={isEditPinned}
                    popoverContainerRef={this.popoverContainerRef.current}
                    {...props}
                  />
                )
              }
            />

            {/* Catch all route */}
            <Route render={this.handleDefaultRoute} />
          </Switch>
        </div>

        <SavePortfolioModal
          show={showSavePortfolioModal}
          onConfirm={this.onConfirmSavePortfolio}
          onDecline={this.onDeclineSavePortfolio}
          onCancel={this.closeSavePortfolioModal}
        />

        <DiscardPortfolioModal show={showDiscardPortfolioModal} onConfirm={this.onConfirmDiscardPortfolio} onCancel={this.closeDiscardPortfolioModal} />

        <DiscardComparisonModal show={showDiscardComparisonModal} onConfirm={this.onCloseComparison} onCancel={this.closeDiscardComparisonModal} />

        {this.props.showLoadClientPortfoliosModal && (
          <LoadPortfoliosModal
            onSort={this.onSortingChange}
            isComparing={isComparingPortfolios}
            queries={queries}
            show={this.props.showLoadClientPortfoliosModal}
            modelPortfolios={modelPortfolios}
            myModelsPortfolios={myModelsPortfolios}
            enterpriseModelsPortfolios={enterpriseModelsPortfolios}
            enterprise={enterprise}
            loadingModels={loading.portfolios}
            hasMoreModelPortfolios={!!nextModelsToken}
            hasMoreMyModelPortfolios={!!nextMyModelsToken}
            hasMoreEnterprisePortfolios={!!nextEnterpriseModelsToken}
            user={user}
            selectedContact={selectedContact}
            loadContactPortfolios={this.props.loadContactPortfolios}
            onLoadMoreModelPortfolios={this.onLoadMoreModelPortfolios}
            onLoadMoreMyModelsPortfolios={this.onLoadMoreMyModelsPortfolios}
            onLoadMoreEnterprisePortfolios={this.onLoadMoreEnterprisePortfolios}
            onLoadMoreContactPortfolios={this.onLoadMoreContactPortfolios}
            onSearchPortfolios={this.onSearchPortfolios}
            onSearchContactPortfolios={this.onSearchContactPortfolios}
            onClose={this.props.closeLoadClientPortfoliosModal}
            resetCurrentContact={this.props.resetCurrentContact}
            addPortfoliosToComparison={this.addPortfoliosToComparison}
            selectedTab={LOAD_PORTFOLIOS_TABS.CONTACTS}
          />
        )}

        {showLoadModelPortfoliosModal && (
          <LoadPortfoliosModal
            onSort={this.onSortingChange}
            isComparing={isComparingPortfolios}
            queries={queries}
            show={showLoadModelPortfoliosModal}
            modelPortfolios={modelPortfolios}
            myModelsPortfolios={myModelsPortfolios}
            enterpriseModelsPortfolios={enterpriseModelsPortfolios}
            enterprise={enterprise}
            loadingModels={loading.portfolios}
            hasMoreModelPortfolios={!!nextModelsToken}
            hasMoreMyModelPortfolios={!!nextMyModelsToken}
            hasMoreEnterprisePortfolios={!!nextEnterpriseModelsToken}
            user={user}
            selectedContact={selectedContact}
            loadContactPortfolios={this.props.loadContactPortfolios}
            onLoadMoreModelPortfolios={this.onLoadMoreModelPortfolios}
            onLoadMoreMyModelsPortfolios={this.onLoadMoreMyModelsPortfolios}
            onLoadMoreEnterprisePortfolios={this.onLoadMoreEnterprisePortfolios}
            onLoadMoreContactPortfolios={this.onLoadMoreContactPortfolios}
            onSearchPortfolios={this.onSearchPortfolios}
            onSearchContactPortfolios={this.onSearchContactPortfolios}
            onClose={this.closeLoadModelPortfoliosModal}
            resetCurrentContact={this.props.resetCurrentContact}
            addPortfoliosToComparison={this.addPortfoliosToComparison}
            selectedTab={LOAD_PORTFOLIOS_TABS.MODELS}
          />
        )}

        <PortfolioUploadModal
          show={showPortfolioUploadModal}
          onCancel={this.handleClosePortfolioUploadModal}
          titleId="Modal.PortfolioUpload.UploadClientPortfolio"
        />
        {canAccessMultiPortfoliosStatementReader(this.props.user) ? (
          <MultiPortfolioAiStatementReaderModal show={showPortfolioAiStatementReaderModal} onCancel={this.handleClosePortfolioUploadModal} locale={locale} />
        ) : (
          <PortfolioAiStatementReaderModal show={showPortfolioAiStatementReaderModal} onCancel={this.handleClosePortfolioUploadModal} locale={locale} />
        )}

        {proposalRequirements && proposalRequirements.length && (
          <ProposalRequirementsModal
            show={showProposalRequirementsModal}
            onConfirm={this.onConfirmProposalRequirements}
            onCancel={this.closeProposalRequirementsModal}
            missingRequirements={proposalRequirements}
          />
        )}

        {showNoAssetClassModal && this.state.noAssetClassModalTab && (
          <NoAssetClassModal
            filteredItems={itemsByTab[this.state.noAssetClassModalTab] || []}
            onCancel={this.closeNoAssetClassModal}
            onConfirm={() => this.onConfirmOpenNoAssetClassModal(this.state.noAssetClassModalTab!)}
            show={showNoAssetClassModal}
            tab={this.state.noAssetClassModalTab}
          />
        )}

        <ESGNoDataModal items={excludedItemsByESG} show={showESGNoDataModal} onConfirm={this.onConfirmOpenESG} onCancel={this.closeESGNoDataModal} />

        {!isExportRequirementModalHidden && (
          <ProposalExportRequirementsModal
            onCancel={() => this.toggleProposalExportRequirementsModal([ProposalExportRequirementsModalDisplayMode.HIDDEN])}
            onClosed={() => this.toggleProposalExportRequirementsModal([ProposalExportRequirementsModalDisplayMode.HIDDEN])}
            onConfirm={this.createProposal}
            onGoToSuitability={() => {
              this.toggleProposalExportRequirementsModal([ProposalExportRequirementsModalDisplayMode.HIDDEN]);
              this.toggleSuitability();
            }}
            items={items}
            type={proposalExportRequirementsDisplayMode}
            user={user}
            portfolioNamesOutsideRiskProfileRange={portfolioNamesOutsideRiskProfileRange}
            show={!isExportRequirementModalHidden}
          />
        )}

        <EditPortfolioModal
          show={isEditVisible && !isEditPinned}
          onSave={this.onSavePortfolio}
          onCancel={this.cancelEditPortfolio}
          saveLabel={portfolioIsNew(editedPortfolio) ? formatMessage('Comparison.AddToComparison') : undefined}
          saveNotification={portfolioIsNew(editedPortfolio) ? formatMessage('HoldingsEditor.Created.PORTFOLIO') : undefined}
          onPin={this.onPinPortfolioEditor}
          onExited={this.handlePendingPortfolios}
          onRerunCurrentComparison={this.onRerunCurrentComparison}
        />

        {portfolioToSave && showAssignPortfolioModal && (
          <AssignPortfolioModal
            user={user}
            portfolio={portfolioToSave}
            show={showAssignPortfolioModal}
            onCancel={this.hideAssignPortfolioModal}
            onSave={this.onConfirmAssignPortfolio}
            requireContact={portfolioSaveToContact}
            currentContact={contact}
            loadingPortfolios={loading.portfolios}
          />
        )}

        {currentRationaleQuestionnaire && (
          <CreateQuestionnaire
            isModal
            show={showSuitabilityModal}
            title={formatMessage('Comparison.Questionnaire.Rationale.QuestionnaireTitle')}
            questionnaire={currentRationaleQuestionnaire}
            onClose={this.toggleSuitability}
            onSave={this.saveRationaleQuestionnaireAnswers}
            questionnaireAnswers={this.props.rationaleAnswers}
            readOnly={readOnly}
            onDelete={this.showDeleteRationaleAnswersModal}
            onEdit={this.editRationaleAnswers}
          />
        )}

        <SuitabilityAnalysisModal
          contactId={comparison.contact?._id}
          portfolios={comparison.items
            .filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO)
            .map(item => {
              const { performance, ...portfolio } = item;

              return portfolio as ComparisonItem;
            })}
          onCancel={() =>
            this.setState({
              suitabilityAnalysisModalDisplayMode: SuitabilityAnalysisModalDisplayMode.HIDDEN,
            })
          }
          show={suitabilityAnalysisModalDisplayMode !== SuitabilityAnalysisModalDisplayMode.HIDDEN}
          submitButton={
            suitabilityAnalysisModalDisplayMode === SuitabilityAnalysisModalDisplayMode.PROPOSAL && (
              <RateLimitedComponent type={RateLimitType.PROPOSALS}>
                {({ disabled, error }) => (
                  <PrimaryButton disabled={disabled || this.createProposalIsDisabled()} onClick={this.onCreatePDFProposal} error={error}>
                    <FormattedMessage id="Comparison.CreateProposal" />
                  </PrimaryButton>
                )}
              </RateLimitedComponent>
            )
          }
        />

        {showMissingLinkedContactModal && (
          <WarningModal
            show
            title={formatMessage('ComparisonMissingLinkedContactsModal.Title')}
            body={formatMessage('ComparisonMissingLinkedContactsModal.Body')}
            onConfirm={this.hideMissingLinkedContactModal}
          />
        )}

        {showDeleteRationaleAnswersModal && (
          <DestructiveModal
            open
            onOpenChange={open => !open && this.toggleDeleteSuitabilityAnswers()}
            title={formatMessage('Comparison.Questionnaire.Delete.ModalTitle')}
            content={formatMessage('Comparison.Questionnaire.Delete.Body')}
            confirmButtonText={formatMessage('Comparison.Questionnaire.Delete.ButtonTitle')}
            cancelButtonText={formatMessage('Common.Cancel')}
            onConfirm={this.deleteRationaleAnswers}
            onCancel={this.toggleDeleteSuitabilityAnswers}
          />
        )}

        {groupItemsToDelete && groupItemsToDelete.length > 0 && (
          <DeleteComparisonItemGroupModal
            groupItems={groupItemsToDelete}
            show={showDeleteGroupModal}
            onConfirm={this.handleDeleteGroups}
            onCancel={this.hideDeleteGroupModal}
          />
        )}

        {showAddGroupModal && (
          <AddComparisonGroupModal
            show={showAddGroupModal}
            onCancel={this.onCancelAddGroup}
            onConfirm={this.onConfirmAddGroup}
            comparisonItems={rawComparison.items}
            groupToEdit={groupToEdit}
          />
        )}

        {showManageAccountSetsModal && (
          <ManageAccountSetsModal
            show={showManageAccountSetsModal}
            onClose={this.hideManageAccountSetsModal}
            onSave={this.onSaveAccountSets}
            initialAccountSets={comparison.accountSets ?? []}
            comparisonItems={rawComparison.items}
          />
        )}

        {comparisonMissingItems && (
          <ComparisonMissingItemsModal
            show={showComparisonMissingItemsModal}
            onCancel={this.handleCancelMissingItemsModal}
            onConfirm={this.removeMissingItemsAndRefreshComparison}
            comparison={comparisonMissingItems}
          />
        )}

        {showMixedCurrencyModal && (
          <MixedCurrencyModal
            show
            isConverting={isConvertingMixedCurrency}
            items={this.getMixedCurrencyItems(items)}
            onConfirm={this.onConfirmMixedCurrency}
            onCancel={this.closeMixedCurrencyModal}
          />
        )}

        {showExportPresentationModal && (
          <ExportPresentationModal
            user={user}
            comparisonId={comparison._id!}
            show={showExportPresentationModal}
            presentationTemplates={presentationTemplates}
            locale={locale}
            onClose={() => this.setState({ showExportPresentationModal: false })}
            onCreatePDFProposal={this.onCreatePDFProposal}
            onCreateOmniProposal={this.onCreateOmniProposal}
            canCreateOmniProposal={{
              isDirty: comparison.isDirty,
              hasContact: !!comparison.contact,
            }}
          />
        )}

        <WarningModal
          show={showCloseComparisonModal}
          onConfirm={this.onCloseComparison}
          onCancel={this.closeCloseComparisonModal}
          title={formatMessage('CloseComparison.Title')}
          body={<FormattedMessage tagName="p" id="CloseComparison.Body" />}
        />

        <StatusFeedbackModal
          show={comparisonIsLoading || isProcessing}
          status={Status.LOADING}
          i18nBasePath={isProcessing ? 'Comparison.IsProcessing' : 'Comparison'}
          isDelayed
        />

        <ComparisonFocusOverlay />
        <ScrollToTop position={this.setScrollToTopPosition('comparison-table')} />
      </>
    );
  }
}

type WrappedComparisonBuilderProps = Omit<ComparisonBuilderProps, 'comparison' | 'rawComparison'> & {
  comparison?: Comparison;
  rawComparison?: Comparison;
};

// Wrapping the ComparisonBuilder component to avoid having to check for a null comparison everywhere.
const WrappedComparisonBuilder = ({
  user,
  currentContact,
  comparison,
  rawComparison,
  createComparison,
  shouldUpdateCommonInception,
  setCommonInceptionItem,
  currentHousehold,
  ...rest
}: WrappedComparisonBuilderProps) => {
  const { isLightVersion } = useThemeVersion();
  const { isPostMessageActive } = rest;
  const { isFFEnabled } = useFeatureFlag();
  const { tabSelections, updateTabSelection } = useComparisonTabSelection();

  useEffect(() => {
    if (!rawComparison) {
      createComparison(user.appConfig.mode === AppMode.CONTACT ? currentContact?.contact : undefined, currentHousehold);
    }

    const timer = setTimeout(() => {
      if (shouldUpdateCommonInception) {
        setCommonInceptionItem();
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [rawComparison, currentContact, createComparison, user, shouldUpdateCommonInception, setCommonInceptionItem, currentHousehold]);

  // This is really just a safety guard - in contact mode you would always
  // get here through a contact.
  if (user.appConfig.mode === AppMode.CONTACT && !isPostMessageActive) {
    if (!currentContact?.contact && !currentHousehold) {
      return <Redirect to={NAV_PATHS.CONTACTS} />;
    }
  }

  if (!comparison || !rawComparison) {
    return null;
  }

  return (
    <ComparisonBuilder
      {...rest}
      user={user}
      currentContact={currentContact}
      comparison={comparison}
      rawComparison={rawComparison}
      currentHousehold={currentHousehold}
      createComparison={createComparison}
      isLightVersion={isLightVersion}
      shouldUpdateCommonInception={shouldUpdateCommonInception}
      setCommonInceptionItem={setCommonInceptionItem}
      isFFEnabled={isFFEnabled}
      tabSelections={tabSelections}
      updateTabSelection={updateTabSelection}
    />
  );
};

const mapStateToProps = (state: ReduxState) => {
  const shelfStatusedComparison = selectShelfStatusedComparison(state);
  const comparison = selectComparisonWithFilteredItems(shelfStatusedComparison);
  const rawComparison = state.comparison.activeComparison;
  return {
    comparison,
    rawComparison,
    currentHousehold: state.households.currentHousehold,
    isProcessing: !!state.ui.loaders.actions.find(loader => [PROCESSING_COMPARISON_ITEM_ACTION.type].includes(loader.name)),
    comparisonIsLoading: !!state.ui.loaders.actions.find(loader => [ADD_FUNDS_TO_COMPARISON.type, ADD_PORTFOLIOS_TO_COMPARISON.type].includes(loader.name)),
    editedPortfolio: state.editPortfolio.portfolio,
    editedComparisonItem: state.editPortfolio.comparisonItem,
    isPortfolioDirty: state.editPortfolio.isDirty,
    canPortfolioSave: !!state.editPortfolio.canSave,
    modelPortfolios: state.portfolios.models,
    myModelsPortfolios: state.portfolios.myModels,
    enterpriseModelsPortfolios: state.portfolios.enterpriseModels,
    enterprise: state.enterprise.details,
    selectedContact: state.contacts.current,
    isEditVisible: state.comparison.portfolioEditor.isVisible,
    isEditPinned: state.comparison.portfolioEditor.isPinned,
    isEditReadOnly: state.comparison.portfolioEditor.isReadOnly,
    locale: state.intl.locale,
    kycQuestionnaires: state.kycQuestionnaire.questionnaires,
    rationaleQuestionnaires: state.rationaleQuestionnaire.questionnaires,
    rationaleCompleted: state.rationaleQuestionnaire.isCompleted,
    rationaleAnswers: state.rationaleQuestionnaire.answers,
    suitabilityAnalysisCompleted: !!state.suitabilityAnalysis,
    fundStore: state.funds.store,
    stockStore: state.stocks.store,
    nextModelsToken: state.portfolios.nextModelsToken,
    nextMyModelsToken: state.portfolios.nextMyModelsToken,
    nextEnterpriseModelsToken: state.portfolios.nextEnterpriseModelsToken,
    emptyStateIsSkipped: state.comparison.skipEmptyState,
    isSaving: state.comparison.isSaving,
    proposalTemplate: state.proposalV2.selectedTemplate,
    proposalTemplates: state.proposalV2.templates,
    editedScenario: state.comparison.editedScenario,
    currentContact: state.contacts.current,
    selectedComparisonItems: state.comparison.selectedComparisonItems,
    itemsFilter: state.comparison.itemsFilter,
    isComparingPortfolios: selectIsLoadingEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]),
    isRerunning: selectIsLoadingSome(state, [SET_IS_RERUNNING.type, LOAD_COMPARISON.type]),
    isAddingToComparison: selectIsLoadingSome(state, [
      ADD_FUND_TO_COMPARISON.type,
      ADD_PORTFOLIO_TO_COMPARISON.type,
      ADD_STOCK_TO_COMPARISON.type,
      ADD_BENCHMARK_PORTFOLIOS_TO_COMPARISON.type,
      ADD_BENCHMARK_TO_COMPARISON.type,
    ]),
    tab: state.comparison.tab,
    originPath: state.comparison.originPath,
    userPreferences: state.auth.user?.preferences,
    showLoadClientPortfoliosModal: state.comparison.showLoadClientPortfoliosModal,
    contacts: state.contacts.contacts,
    shouldUpdateCommonInception: state.comparison.shouldUpdateCommonInception,
    isPostMessageActive: state.postMessage.isPostMessageActive,
    presentationTemplates: state.auth.user?.enterprise?.presentations?.templates ?? [],
    postMessagePortfolios: state.contacts.postMessagePortfolios,
    shouldCloseEditor: shouldClosePortfolioEditor(state.aiStatementReaderMultiPortfolio),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    addPortfoliosToComparison,
    applyMarkupToCell,
    clearAllMarkup,
    clearMarkup,
    clonePortfolio,
    cloneBenchmark,
    closeComparison,
    combineIntoPortfolio,
    getComparisonById,
    validateComparisonToken,
    createComparison,
    getModelsPortfolios,
    getPortfolioById,
    getKycQuestionnaires,
    loadOmniRiskProfilesQuestionnaire,
    getQuestionnaires,
    getQuestionnaireAnswersById,
    initLoadingPortfolios,
    loadComparison,
    loadContactPortfolios,
    getContactPortfolios,
    loadNewPortfolio,
    loadPortfolioForEdit,
    loadProposalTemplates,
    removeItem,
    replacePortfolioInComparison,
    rerunComparison,
    resetCurrentContact,
    resetPortfolio,
    resetProposal,
    resetSearch,
    saveComparison,
    savePortfolio,
    selectComparisonItem,
    setChartOptions,
    setComparisonShouldForceMonthlyData,
    setComparisonContact,
    setComparisonName,
    setExpandPortfolioItem,
    setPortfolioContact,
    setRiskPeriod,
    setSelectedCategories,
    setTab,
    shareComparison,
    togglePortfolioEditor,
    updateSelectedCategories,
    toggleShortHistory,
    loadBenchmarkForEdit,
    getBenchmarkPortfolio,
    saveBenchmark,
    replaceBenchmarkInComparison,
    setProposalTemplate,
    calculateIncomeStream,
    consumeLimit,
    showBaseCurrencies,
    setMonthEndDataOption,
    saveQuestionnaireAnswers,
    updateQuestionnaireAnswers,
    deleteQuestionnaireAnswers,
    saveModelCopyToDraftPortfolio,
    addRationaleAnswers,
    getContactById,
    addItemToGroup,
    removeItemFromGroup,
    updateComparisonGroup,
    addItemAndRecalculateComparison,
    addGroupToComparison,
    setComparisonItemsFilter,
    changeItemId,
    addCategoryBenchmark,
    unselectComparisonItem,
    removeMissingComparisonData,
    skipEmptyState,
    toggleCategory,
    setOriginPath,
    attachRiskProfile,
    openLoadClientPortfoliosModal,
    closeLoadClientPortfoliosModal,
    setCommonInceptionItem,
    sendPostMessageGetContactsAndPortfolios,
    setCurrentContact,
    createOmniExport,
    getItemHoldingsData,
    startLoaderAction,
    stopLoaderAction,
    setPortfolioEditorIsCreatingModel,
    setShowTaxDetails,
    updateAccountSets,
  })(WrappedComparisonBuilder),
);
